import * as PIXI from "pixi.js";
import { queueIdleAfterLanding } from "../../Shared/landIdleJoin";
import { Spine } from "@esotericsoftware/spine-pixi-v7";
import { symbolData, BLANK_SYMBOL_ID } from "../../../SymbolData";
import { Settings, GAME_LAYOUT } from "../../../settings";
import { COIN_SYMBOL_IDS, COIN_SYMBOL_ID } from "../../../SymbolData";
import store from "../../../State/Store/store";
import { cosmeticCoinValue, coinValueAt } from "../../Shared/coinValues";
import { wearCoinValue } from "../../Shared/coinValueLabel";
import { formatToCustomLocale } from "../../../utilities";
import { soundManager } from "../../../SoundManager";
import { SOUND_NAME } from "../../../sounds";
import { FEATURES_CONFIG } from "../../../featuresConfig";
import { queueMeshWarm } from "../../Shared/spineMeshWarm";
import SpineAtlasUploader from "../../../utils/SpineAtlasUploader";
import RevealSymbolController from "../../Shared/RevealSymbolController";
import GoldenMysteryCellController from "../../Shared/GoldenMysteryCellController";
import {
  buildGoldenMysteryConfig,
  buildGoldenMysterySpine,
  buildFeatureSpine,
} from "../Mystery/goldenMysteryConfig";
import {
  buildGambleScatterConfig,
  toStripItem as gambleToStripItem,
  toStaticCell as gambleToStaticCell,
} from "../GambleScatter/gambleScatterConfig";
import {
  buildPlusScatterConfig,
  toStripItem as plusToStripItem,
  toStaticCell as plusToStaticCell,
} from "../GambleScatter/plusScatterConfig";
import {
  SCATTER_2_SYMBOL_ID,
  SCATTER_3_SYMBOL_ID,
  MYSTERY_SYMBOL_ID,
  GOLDEN_FRAME_SYMBOL_ID,
  symbolData as revealSymbolData,
} from "../../../SymbolData";

// The H&W grid-only symbols' pays — no spine is built for them on the reels.
const HOLD_AND_WIN_GRID_ONLY_PAYS = new Set(["blank", "chest", "golden_chest", "scarab"]);

/**
 * Symbols that run a reveal sequence (RevealSymbolController) instead of a
 * single clip. Their ids are null in this game (Mad Labs symbols, see
 * SymbolData), so no real symbol matches these entries.
 *
 *   buildConfig(cellW, cellH)  the RevealSymbolController config
 *   toStripItem(result)        the round's payload as a strip item
 *   toStaticCell(item)         static texture the settled cell keeps, or null
 */
const REVEAL_SYMBOLS = {
  // `+FS` scatter (free spins only): lands as the golden scatter, opens, and
  // spins a +1/+2/+3 strip inside (see plusScatterConfig). `hostSymbolId`
  // makes it drive SC1's pooled `scg` spine instead of its own `bs`.
  [SCATTER_3_SYMBOL_ID]: {
    hostSymbolId: SCATTER_2_SYMBOL_ID,
    buildConfig: buildPlusScatterConfig,
    toStripItem: plusToStripItem,
    toStaticCell: plusToStaticCell,
  },

  // SC1 — lands, opens, spins the free-spin packages inside itself, settles on
  // the one the backend awarded.
  [SCATTER_2_SYMBOL_ID]: {
    buildConfig: buildGambleScatterConfig,
    toStripItem: gambleToStripItem,
    toStaticCell: gambleToStaticCell,
  },

  // B1 mystery: lands and opens onto the symbol the backend chose. No strip,
  // nothing injected; the host fades out. toStaticCell is null because the
  // cell underneath is already painted with the revealed symbol (see the
  // mystery block in useWinController).
  [MYSTERY_SYMBOL_ID]: {
    buildConfig: () => ({
      ...revealSymbolData[MYSTERY_SYMBOL_ID].reveal,
    }),
    // The replacement id, straight through — it is what the doors open onto.
    toStripItem: (replacementId) => replacementId,
    toStaticCell: () => null,
  },
};

/**
 * Per-reel controller for the base game's spine overlay, drawn above the
 * spinning reels. Every cell holds one pooled spine per symbol, keyed
 * `${symbolIndex}_${symbolId}`, shown at alpha 1 while animating and parked at
 * alpha 0.001 (invisible, not ticking) otherwise. Handles landing / idle / win
 * clips, coin value labels, the full-reel expanding wild, reveal symbols and
 * Golden Mystery cells.
 *
 * Works in native units: a cell is Settings.SYMBOL_WIDTH x SYMBOL_HEIGHT, and
 * the container inherits the game view spine's scale, so there is no resize path.
 */
class OverlayReelController {
  constructor(
    overlaySymbolRefs,
    reelIndex,
    symbolWidth,
    symbolHeight,
    reelContainer = null,
    bet = 1,
  ) {
    this.overlaySymbolRefs = overlaySymbolRefs;
    this.reelIndex = reelIndex;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.reelContainer = reelContainer;
    this.bet = bet || 1; // Store bet value for coin calculations
    this.prePositionedSpines = new Map();
    this.activeAnimations = new Map();

    // Landings still playing, keyed by symbolIndex (see waitForLandings). The
    // win clip goes on the same track 0 and would cut a bounce, so wins wait
    // for these. A cell with no landing clip never registers.
    this._pendingLandings = new Set();
    this._landingWaiters = [];
    this.animationCache = new Map();

    // Available animation names per symbol, keyed `0_<symbolId>`
    this.animationAvailability = new Map();

    // Internal caches for performance optimization
    this._spineScaleCache = new Map(); // Cache for calculateSpineScale results
    this._expandingWildScaleCache = null; // Cache for expanding wild scale
    this._coinDisplayValueCache = new Map(); // Cache for coin display values

    // Expanding wild full-reel spine properties
    this.expandingWildSpine = null;
    this.expandingWildSpineData = {
      skeleton: "wild_expand-skeleton",
      atlas: "wild_expand-atlas",
    };
    this.expandingWildTimeouts = [];
    this.isExpandingWildVisible = false;
    this.expandingWildInitialized = false;
    this.currentExpandingWildAnimation = null; // Track current animation ("appear", "idle", or "win")

    // Expanding wild scale / offset multipliers (see applyExpandingWildScaling)
    this.expandingWildHeightMultiplier = 1.0;
    this.expandingWildYOffsetMultiplier = 0;

    // Coin value labels, worn on the coin spine's slot
    this.coinValueContainers = new Map(); // symbolIndex → { counterContainer, bitmapTexts, spineInstance, slotName, displayValue }

    // Track pending requestAnimationFrame handles for cleanup
    this._pendingRAF = null;

    this.initializeAllSpineInstances();

    // Reel 0 builds and warms the expanding wild up front (shader compilation
    // is global); other reels build it lazily on first play.
    if (this.reelIndex === 0 && this.reelContainer) {
      this.initializeExpandingWildSpine();
    }
  }

  getShadowReelController() {
    return this;
  }

  /**
   * Get the expanding wild spine instance for event listening
   * @returns {Spine|null} The expanding wild spine instance
   */
  getExpandingWildSpine() {
    return this.expandingWildSpine;
  }

  /**
   * The coin's own value before the bet. Always 0 here: coin `value` in
   * symbolData is null on purpose, because coin values come from the API per
   * cell (see coinValueAt).
   */
  _getCoinValue(symbolId) {
    const raw = symbolData[symbolId]?.value;
    const value = parseInt(raw, 10);
    return Number.isFinite(value) && value > 0 ? value : 0;
  }

  /**
   * Coin display value (coinValue x bet), formatted; "" when the symbol has no
   * value, which is always the case in this game (see _getCoinValue).
   * @param {number} symbolId - Symbol ID
   * @param {number|null} betOverride - Optional bet value for error recovery (ONE TIME use)
   */
  _getCoinDisplayValue(symbolId, betOverride = null) {
    const symbolValue = symbolData[symbolId]?.value;
    if (!symbolValue || symbolValue === null) {
      return "";
    }

    try {
      const coinValue = parseInt(symbolValue, 10);

      if (isNaN(coinValue) || coinValue <= 0) {
        return "";
      }

      // Use bet override if provided (error recovery), otherwise use this.bet
      const bet = betOverride !== null ? betOverride : this.bet;

      const displayValue = coinValue * bet;
      return formatToCustomLocale(displayValue);
    } catch (error) {
      return "";
    }
  }

  /**
   * Build a BitmapText coin label in a container, scaled down uniformly to fit
   * the cell width. showCoinValueText does not use it: it wears the value on
   * the coin's own slot (wearCoinValue).
   */
  createCoinValueText(displayValue) {
    if (!displayValue || displayValue === "") {
      return null;
    }

    const counterContainer = new PIXI.Container();
    counterContainer.name = "coin_value_text";
    counterContainer.alpha = 1;
    counterContainer.visible = true;
    counterContainer.zIndex = 1000; // Render above spine effects

    // Font size follows the cell height (same multiplier as ReelControllerV2).
    const fontSize =
      this.symbolHeight * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER;

    // Create BitmapText for the value (target native size: 34px)
    const bitmapText = new PIXI.BitmapText(displayValue, {
      fontName: "font_secondary_scaled",
      fontSize: fontSize,
    });

    bitmapText.anchor.set(0.5);
    bitmapText.x = 0;
    bitmapText.y = 0;
    bitmapText.zIndex = 1;
    bitmapText.roundPixels = true;

    // Force update to get accurate measurements
    bitmapText.updateText();

    // Calculate available width (with padding)
    const coinWidth = this.symbolWidth;
    const paddingPercent = 0.12; // 12% padding on each side
    const maxTextWidth = coinWidth * (1 - paddingPercent * 2);

    const actualTextWidth = bitmapText.width;

    // If text is too wide, scale down uniformly to fit (preserves aspect ratio)
    if (actualTextWidth > maxTextWidth) {
      const scale = maxTextWidth / actualTextWidth;
      bitmapText.scale.set(scale);
    }

    counterContainer.addChild(bitmapText);

    return { counterContainer, bitmapTexts: [bitmapText] };
  }

  /**
   * Show coin value text for a coin symbol
   * @param {number} symbolIndex - Position index
   * @param {number} symbolId - Symbol ID
   * @param {object} spineInstance - Spine instance
   * @param {number|null} betOverride - Optional bet value for error recovery
   */
  showCoinValueText(symbolIndex, symbolId, spineInstance, betOverride = null) {
    // Skip entirely when coins don't appear on base game reels
    if (!FEATURES_CONFIG.SPIN.COIN_VALUES_ON_REELS) return;

    // Check if it's a coin symbol
    if (!COIN_SYMBOL_IDS.includes(symbolId)) {
      return;
    }

    // Check if coin value text already exists for this symbol at this position
    const existingCoinData = this.coinValueContainers.get(symbolIndex);
    // Keep the existing label only if it sits on THIS spine with THIS round's
    // number; a label left by a hide path that skips hideCoinValueText must not
    // carry over. A round with no number (cosmetic) keeps what is worn, so the
    // value does not re-roll and flicker.
    const roundCoinValue = coinValueAt(
      store.getState().game?.currentRound,
      this.reelIndex,
      symbolIndex,
    );
    if (
      existingCoinData &&
      existingCoinData.counterContainer &&
      !existingCoinData.counterContainer.destroyed &&
      existingCoinData.spineInstance === spineInstance &&
      (roundCoinValue == null || existingCoinData.displayValue === roundCoinValue)
    ) {
      return;
    }

    this.hideCoinValueText(symbolIndex);

    // Wear the round's value for this cell (round.coinValues, the same number
    // the landing picture showed) on the coin's own slot; a cosmetic value only
    // when the round has none (dev simulation). Plain coins only: the jackpot
    // coin's face is its tier word.
    if (symbolId !== COIN_SYMBOL_ID || !spineInstance) return;
    const value = roundCoinValue ?? cosmeticCoinValue(betOverride ?? undefined);
    const worn = wearCoinValue(spineInstance, value);
    if (!worn) return;
    this.coinValueContainers.set(symbolIndex, {
      counterContainer: worn.label,
      bitmapTexts: [],
      spineInstance,
      slotName: worn.slotName,
      displayValue: value,
    });
  }

  /**
   * Hide coin value text for a symbol
   */
  hideCoinValueText(symbolIndex) {
    const coinData = this.coinValueContainers.get(symbolIndex);
    if (!coinData) {
      return;
    }

    // Detach from spine slot BEFORE destroying (prevents localTransform error)
    if (coinData.spineInstance && coinData.counterContainer && !coinData.counterContainer.destroyed) {
      try {
        coinData.spineInstance.removeSlotObject(
          // The slot the label was worn on (stored when it was added).
          coinData.slotName,
          coinData.counterContainer,
        );
      } catch {
        // Silent — spine may already be destroyed
      }
    }

    // Destroy bitmap texts
    if (coinData.bitmapTexts) {
      coinData.bitmapTexts.forEach((text) => {
        if (text && !text.destroyed) {
          text.destroy();
        }
      });
    }

    // Destroy container
    if (coinData.counterContainer && !coinData.counterContainer.destroyed) {
      if (coinData.counterContainer.parent) {
        coinData.counterContainer.parent.removeChild(coinData.counterContainer);
      }
      coinData.counterContainer.destroy({ children: true });
    }

    // Remove from map
    this.coinValueContainers.delete(symbolIndex);
  }

  /**
   * Hide all coin value texts
   */
  hideAllCoinValueTexts() {
    const symbolIndices = Array.from(this.coinValueContainers.keys());
    symbolIndices.forEach((symbolIndex) => {
      this.hideCoinValueText(symbolIndex);
    });
  }

  /**
   * Store the new bet. Labels already on the reels are not refreshed.
   */
  updateBet(newBet) {
    const oldBet = this.bet;
    this.bet = newBet || 1; // Store new bet value for next spin

    // Invalidate coin display value cache when bet changes
    if (oldBet !== this.bet) {
      this._coinDisplayValueCache.clear();
    }

    // Labels already on the reels belong to the finished round; new values
    // arrive with the next landing.
    return;
  }

  // Whether this symbol's spine has a clip of exactly this name (the turbo
  // `static`, ported from Catch the Cod, 2026-10-28).
  hasClip(symbolId, name) {
    const available = this.animationAvailability.get(`0_${symbolId}`);
    return !!available?.has(name);
  }

  /**
   * Which landing / idle / appear clips this symbol's spine has, from the
   * per-symbol cache, gated by the SPINE_ANIMATIONS feature flags.
   */
  getAvailableAnimations(symbolIndex, symbolId) {
    // Texture-only game optimization - skip landing/idle checks if disabled
    if (
      !FEATURES_CONFIG.SPINE_ANIMATIONS.LANDING_ENABLED &&
      !FEATURES_CONFIG.SPINE_ANIMATIONS.IDLE_ENABLED
    ) {
      return { hasLanding: false, hasIdle: false, hasAppear: true };
    }

    // Always look up using position 0 since all positions share the same symbol's animations
    const warmupKey = `0_${symbolId}`;
    const available = this.animationAvailability.get(warmupKey);

    if (!available) {
      return { hasLanding: false, hasIdle: false, hasAppear: false };
    }

    let hasLanding = false;
    let hasIdle = false;
    let hasAppear = false;

    for (const name of available) {
      // Landing clip names vary per spine: "in", "land", "landing", "bounce".
      // Matched lower-case. "in" is exact-only — as a substring it would match
      // "win" and every symbol would claim a landing animation.
      const lower = name.toLowerCase();
      if (
        !hasLanding &&
        (lower.includes("land") || lower.includes("bounce") || lower === "in")
      ) {
        hasLanding = true;
      }
      // Treat "static" as an idle-equivalent (e.g. jackpot spine j.json has no "idle" but has "static")
      if (!hasIdle && (name.includes("idle") || name === "static")) hasIdle = true;
      if (!hasAppear && name.includes("appear")) hasAppear = true;
      if (hasLanding && hasIdle && hasAppear) break;
    }

    // Apply feature flags to results
    return {
      hasLanding:
        FEATURES_CONFIG.SPINE_ANIMATIONS.LANDING_ENABLED && hasLanding,
      hasIdle: FEATURES_CONFIG.SPINE_ANIMATIONS.IDLE_ENABLED && hasIdle,
      hasAppear,
    };
  }

  // Whether this symbol's spine has an idle (or static) clip.
  hasIdleAnimationAt(symbolIndex, symbolId) {
    const { hasIdle } = this.getAvailableAnimations(symbolIndex, symbolId);
    return hasIdle;
  }

  // Show the win clip on this cell's pooled spine. A spine with no win clip
  // plays idle instead, so the symbol still looks alive.
  // @param {number|null} betOverride - Optional bet value for error recovery
  // @param {number} targetLoops - Number of loops (0=infinite, 1=play once and hold, N=play N times and hold)
  showWinAnimation(
    symbolIndex,
    symbolId,
    animationType = "win",
    betOverride = null,
    targetLoops = 0,
  ) {
    const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
    if (!overlaySymbol) return;

    // Golden Mystery cell: it owns its whole picture (frame, lid, outcome), and
    // its sprite still claims the mystery's id, so the ordinary path would reset
    // the doors shut. It is asked to celebrate instead (`my.win` on its own
    // track, see GoldenMysteryCellController.playWin).
    if (this.hasGoldenMystery(symbolIndex)) {
      const cell = this.goldenControllers?.get(symbolIndex);
      // Wake it first: between lines it is parked and the reel's flat copy
      // stands in for it (see setCellVisible).
      cell?.setCellVisible?.(true);
      // The line's own loop count, so it animates as long as the other symbols.
      cell?.playWin?.(targetLoops);
      return;
    }

    const spineKey = `${symbolIndex}_${symbolId}`;
    const spineInstance = this.prePositionedSpines.get(spineKey);
    if (!spineInstance) return;

    const existingAnimation = this.activeAnimations.get(symbolIndex);

    // Early return if SAME symbol and animation type already playing
    // Prevents flicker on shared symbols across multiple winning lines during cycling
    if (
      existingAnimation &&
      existingAnimation.symbolId === symbolId &&
      existingAnimation.animationType === animationType
    ) {
      // Symbol already playing - don't restart! Just ensure visibility
      spineInstance.alpha = 1;
      spineInstance.visible = true;
      overlaySymbol.alpha = 1;
      overlaySymbol.visible = true;
      return;
    }

    // Also skip restart when existing is "idle" from a win-to-idle fallback
    // and the incoming request would also produce the same fallback.
    // Without this, symbols without a dedicated win clip restart every cycle
    // while symbols with win clips stay stable (dedup above catches them).
    if (
      existingAnimation &&
      existingAnimation.symbolId === symbolId &&
      existingAnimation.animationType === "idle" &&
      (animationType === "win" || animationType === "win_fast")
    ) {
      // Check if this request would also fall back to idle
      const candidateAnim = this.selectSpineAnimation(spineInstance, animationType, symbolId);
      if (candidateAnim && !candidateAnim.toLowerCase().includes("win")) {
        // Same fallback — don't restart, just ensure visibility
        spineInstance.alpha = 1;
        spineInstance.visible = true;
        overlaySymbol.alpha = 1;
        overlaySymbol.visible = true;
        return;
      }
    }

    // Only hide if DIFFERENT symbol or animation type
    if (existingAnimation) {
      this.hideWinAnimation(symbolIndex);
    }

    // Always reset bones (clears stale scale/position from prior animations).
    // Only reset slots when hidden (preserves inherited attachments when visible).
    if (spineInstance.skeleton) {
      spineInstance.skeleton.setBonesToSetupPose();
      if (spineInstance.alpha < 0.5) {
        spineInstance.skeleton.setSlotsToSetupPose();
      }
    }

    let selectedAnimation = this.selectSpineAnimation(
      spineInstance,
      animationType,
      symbolId,
    );

    // Win-to-idle fallback: if we asked for "win" but the resolved animation
    // doesn't actually contain "win" (selectSpineAnimation fell back to a
    // generic clip), play idle instead so the symbol still looks correct.
    let effectiveAnimationType = animationType;
    if (
      animationType === "win" &&
      selectedAnimation &&
      !selectedAnimation.toLowerCase().includes("win")
    ) {
      const idleAnimation = this.selectSpineAnimation(spineInstance, "idle", symbolId);
      if (idleAnimation) {
        selectedAnimation = idleAnimation;
        effectiveAnimationType = "idle";
      }
    }

    const shouldLoop = targetLoops === 0 || targetLoops > 1;
    if (spineInstance.state && selectedAnimation) {
      spineInstance.state.clearTracks();
      const entry = spineInstance.state.addAnimation(0, selectedAnimation, shouldLoop, 0);

      // For finite loops > 1, count completions and stop looping at target
      if (targetLoops > 1 && entry) {
        let completedLoops = 0;
        entry.listener = {
          complete: () => {
            completedLoops++;
            if (completedLoops >= targetLoops) {
              entry.loop = false; // Hold on last frame
            }
          },
        };
      }
    }

    // Make visible AFTER animation is queued — prevents stale-pose flash.
    // Force first frame to apply so the skeleton pose matches the new animation
    // before any pixel renders. Without this, setup/stale pose shows for 1 frame.
    spineInstance.state?.update(0);
    spineInstance.state?.apply(spineInstance.skeleton);
    spineInstance.alpha = 1;
    spineInstance.visible = true;
    spineInstance.autoUpdate = true;
    overlaySymbol.alpha = 1;
    overlaySymbol.visible = true;

    this.activeAnimations.set(symbolIndex, {
      spineInstance,
      symbolId,
      animationType: effectiveAnimationType,
    });

    // Show coin value text if this is a coin symbol (pass bet override for recovery)
    this.showCoinValueText(symbolIndex, symbolId, spineInstance, betOverride);
  }

  /**
   * Resolve when this cell's looping overlay clip reaches the end of its
   * current pass (its seam), so a pose change there does not cut a lit frame.
   * Same wait as RevealSymbolController._waitForLoopCycle. Resolves at once
   * when nothing loops, and on `end` too, so a track cleared by spin start
   * cannot leave the caller waiting.
   *
   * @param   {number} symbolIndex
   * @returns {Promise<void>}
   */
  waitForLoopSeam(symbolIndex) {
    const state = this.activeAnimations.get(symbolIndex)?.spineInstance?.state;
    const entry = state?.tracks?.[0];
    if (!state || !entry?.loop) return Promise.resolve();

    return new Promise((resolve) => {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        state.removeListener(listener);
        resolve();
      };
      const listener = {
        complete: (completed) => {
          if (completed === entry) finish();
        },
        end: (ended) => {
          if (ended === entry) finish();
        },
      };
      state.addListener(listener);
    });
  }

  /**
   * Sync/restart win animation to frame 0 without hiding (keeps animations in sync across lines).
   * If the spine has no actual win animation, falls back to idle (same logic as showWinAnimation).
   * Unlike showWinAnimation, this ALWAYS restarts even if same animation is playing.
   * @param {number} targetLoops - Number of loops (0=infinite, 1=play once and hold, N=play N times and hold)
   */
  syncWinAnimation(
    symbolIndex,
    symbolId,
    animationType = "win",
    betOverride = null,
    targetLoops = 0,
  ) {
    const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
    if (!overlaySymbol) return;

    // Golden Mystery cell: it owns its whole picture (frame, lid, outcome), and
    // its sprite still claims the mystery's id, so the ordinary path would reset
    // the doors shut. It is asked to celebrate instead (`my.win` on its own
    // track, see GoldenMysteryCellController.playWin).
    if (this.hasGoldenMystery(symbolIndex)) {
      const cell = this.goldenControllers?.get(symbolIndex);
      // Wake it first: between lines it is parked and the reel's flat copy
      // stands in for it (see setCellVisible).
      cell?.setCellVisible?.(true);
      // The line's own loop count, so it animates as long as the other symbols.
      cell?.playWin?.(targetLoops);
      return;
    }

    const spineKey = `${symbolIndex}_${symbolId}`;
    const spineInstance = this.prePositionedSpines.get(spineKey);
    if (!spineInstance) return;

    // Reset bones before starting new animation (clears stale scale from prior anim)
    if (spineInstance.skeleton?.setBonesToSetupPose) {
      spineInstance.skeleton.setBonesToSetupPose();
    }

    // Ensure visibility
    spineInstance.alpha = 1;
    spineInstance.visible = true;
    spineInstance.autoUpdate = true;
    overlaySymbol.alpha = 1;
    overlaySymbol.visible = true;

    let selectedAnimation = this.selectSpineAnimation(
      spineInstance,
      animationType,
      symbolId,
    );

    // Win-to-idle fallback: same logic as showWinAnimation.
    // If selectSpineAnimation fell back to a non-win clip, play idle instead.
    let effectiveAnimationType = animationType;
    if (
      animationType === "win" &&
      selectedAnimation &&
      !selectedAnimation.toLowerCase().includes("win")
    ) {
      const idleAnimation = this.selectSpineAnimation(spineInstance, "idle", symbolId);
      if (idleAnimation) {
        selectedAnimation = idleAnimation;
        effectiveAnimationType = "idle";
      }
    }

    const shouldLoop = targetLoops === 0 || targetLoops > 1;

    // Reset animation to frame 0 without hiding
    if (spineInstance.state && selectedAnimation) {
      // Get current track (if exists)
      const currentTrack = spineInstance.state.tracks[0];

      // If already playing the same animation, just reset time
      if (currentTrack && currentTrack.animation.name === selectedAnimation) {
        currentTrack.trackTime = 0;
        currentTrack.loop = shouldLoop;
        // Re-attach loop control listener for finite loops
        if (targetLoops > 1) {
          let completedLoops = 0;
          currentTrack.listener = {
            complete: () => {
              completedLoops++;
              if (completedLoops >= targetLoops) {
                currentTrack.loop = false;
              }
            },
          };
        } else {
          currentTrack.listener = null;
        }
      } else {
        // Different animation, need to switch
        spineInstance.state.clearTracks();
        const entry = spineInstance.state.addAnimation(0, selectedAnimation, shouldLoop, 0);

        if (targetLoops > 1 && entry) {
          let completedLoops = 0;
          entry.listener = {
            complete: () => {
              completedLoops++;
              if (completedLoops >= targetLoops) {
                entry.loop = false;
              }
            },
          };
        }
      }
    }

    this.activeAnimations.set(symbolIndex, {
      spineInstance,
      symbolId,
      animationType: effectiveAnimationType,
    });

    // Show coin value text if this is a coin symbol
    this.showCoinValueText(symbolIndex, symbolId, spineInstance, betOverride);
  }

  // Landing clip, then idle. With ROUND_LEVEL_IDLE the landing holds its last
  // frame and idle starts once all reels have stopped.
  // @param {number|null} betOverride - Optional bet value for error recovery
  showLandingAnimation(symbolIndex, symbolId, betOverride = null) {
    // The Golden Mystery owns this cell — see showWinAnimation.
    if (this.hasGoldenMystery(symbolIndex)) return;

    const spineKey = `${symbolIndex}_${symbolId}`;

    const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
    if (!overlaySymbol) {
      return;
    }

    const spineInstance = this.prePositionedSpines.get(spineKey);
    if (!spineInstance) {
      return;
    }

    const { hasLanding, hasIdle } = this.getAvailableAnimations(
      symbolIndex,
      symbolId,
    );

    if (!hasLanding && !hasIdle) {
      return;
    }

    const existingAnimation = this.activeAnimations.get(symbolIndex);
    if (existingAnimation && existingAnimation.animationType === "win") {
      this.hideWinAnimation(symbolIndex);
    }

    // Always reset bones (clears stale scale/position from prior animations).
    // Only reset slots when hidden (preserves inherited attachments when visible).
    if (spineInstance.skeleton) {
      spineInstance.skeleton.setBonesToSetupPose();
      if (spineInstance.alpha < 0.5) {
        spineInstance.skeleton.setSlotsToSetupPose();
      }
    }

    if (hasLanding && hasIdle) {
      const landingAnimation = this.selectSpineAnimation(
        spineInstance,
        "land",
        symbolId,
      );
      const idleAnimation = this.selectSpineAnimation(
        spineInstance,
        "idle",
        symbolId,
      );

      if (spineInstance.state && landingAnimation && idleAnimation) {
        spineInstance.state.clearTracks();
        this._trackLanding(
          symbolIndex,
          spineInstance.state.addAnimation(0, landingAnimation, false, 0),
        );
        // Round-level idle: landing holds on last frame; idle triggers after ALL reels stop
        if (!FEATURES_CONFIG.SPINE_ANIMATIONS.ROUND_LEVEL_IDLE) {
          // spineInstance.state.addAnimation(0, idleAnimation, true, 0);
          // From the landing's end pose — one movement (Games/Shared/landIdleJoin).
          queueIdleAfterLanding(spineInstance, idleAnimation);
        } else {

        }
      }
    } else if (hasIdle) {
      // Round-level idle: idle-only symbols stay as static texture until round-level trigger
      if (FEATURES_CONFIG.SPINE_ANIMATIONS.ROUND_LEVEL_IDLE) {

        // Reset spine visibility — don't activate the overlay
        spineInstance.alpha = 0.001;
        spineInstance.autoUpdate = false;
        overlaySymbol.alpha = 0;
        return;
      }
      const idleAnimation = this.selectSpineAnimation(
        spineInstance,
        "idle",
        symbolId,
      );
      if (spineInstance.state && idleAnimation) {
        spineInstance.state.clearTracks();
        spineInstance.state.addAnimation(0, idleAnimation, true, 0);
      }
    } else if (hasLanding) {
      const landingAnimation = this.selectSpineAnimation(
        spineInstance,
        "land",
        symbolId,
      );
      if (spineInstance.state && landingAnimation) {
        spineInstance.state.clearTracks();
        this._trackLanding(
          symbolIndex,
          spineInstance.state.addAnimation(0, landingAnimation, true, 0),
        );
        if (FEATURES_CONFIG.SPINE_ANIMATIONS.ROUND_LEVEL_IDLE) {

        }
      }
    }

    // Make visible AFTER animation is queued — prevents stale-pose flash.
    // Force first frame — use same time step (1/60) as texture extraction
    // so the overlay's initial pose exactly matches the static texture.
    spineInstance.state?.update(1 / 60);
    spineInstance.state?.apply(spineInstance.skeleton);
    spineInstance.alpha = 1;
    spineInstance.visible = true;
    spineInstance.autoUpdate = true;
    overlaySymbol.alpha = 1;
    overlaySymbol.visible = true;

    // When round-level idle is active and we have landing, record as "landing" (not "idle")
    // so the post-spin logic knows landing is still holding and can trigger idle explicitly
    const isRoundLevelHolding = FEATURES_CONFIG.SPINE_ANIMATIONS.ROUND_LEVEL_IDLE && hasLanding;
    const recordedType = isRoundLevelHolding
      ? "landing"
      : hasLanding && hasIdle ? "idle" : hasIdle ? "idle" : "landing";

    this.activeAnimations.set(symbolIndex, {
      spineInstance,
      symbolId,
      animationType: recordedType,
      hadLandingAnimation: hasLanding,
    });

    // Show coin value text if this is a coin symbol (pass bet override for recovery)
    this.showCoinValueText(symbolIndex, symbolId, spineInstance, betOverride);
  }

  /**
   * Landing clip followed by a looping win clip (instead of idle).
   * @param {number} symbolIndex
   * @param {number} symbolId
   * @param {number|null} betOverride
   */
  showLandingToWinAnimation(symbolIndex, symbolId, betOverride = null) {
    const spineKey = `${symbolIndex}_${symbolId}`;
    const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
    if (!overlaySymbol) return;

    const spineInstance = this.prePositionedSpines.get(spineKey);
    if (!spineInstance) return;

    const { hasLanding } = this.getAvailableAnimations(symbolIndex, symbolId);

    const existingAnimation = this.activeAnimations.get(symbolIndex);
    if (existingAnimation && existingAnimation.animationType === "win") {
      this.hideWinAnimation(symbolIndex);
    }

    // Always reset bones (clears stale scale/position from prior animations).
    // Only reset slots when hidden (preserves inherited attachments when visible).
    if (spineInstance.skeleton) {
      spineInstance.skeleton.setBonesToSetupPose();
      if (spineInstance.alpha < 0.5) {
        spineInstance.skeleton.setSlotsToSetupPose();
      }
    }

    const winAnimation = this.selectSpineAnimation(spineInstance, "win", symbolId);

    if (hasLanding && winAnimation) {
      const landingAnimation = this.selectSpineAnimation(spineInstance, "land", symbolId);
      if (spineInstance.state && landingAnimation) {
        spineInstance.state.clearTracks();
        spineInstance.state.addAnimation(0, landingAnimation, false, 0);
        spineInstance.state.addAnimation(0, winAnimation, true, 0);
      }
    } else if (winAnimation) {
      if (spineInstance.state) {
        spineInstance.state.clearTracks();
        spineInstance.state.addAnimation(0, winAnimation, true, 0);
      }
    }

    // Make visible AFTER animation is queued
    spineInstance.state?.update(0);
    spineInstance.state?.apply(spineInstance.skeleton);
    spineInstance.alpha = 1;
    spineInstance.visible = true;
    spineInstance.autoUpdate = true;
    overlaySymbol.alpha = 1;
    overlaySymbol.visible = true;

    this.activeAnimations.set(symbolIndex, {
      spineInstance,
      symbolId,
      animationType: "win",
    });

    this.showCoinValueText(symbolIndex, symbolId, spineInstance, betOverride);
  }

  // Show the looping idle clip.
  // @param {number|null} betOverride - Optional bet value for error recovery
  // @param {boolean} frozen - Set the idle pose without animating (autoUpdate=false).
  //   Used during per-reel landing so the texture->spine swap happens under the
  //   reel bounce. unfreezeIdleAnimations() starts it later.
  // showIdleAnimation(symbolIndex, symbolId, betOverride = null, frozen = false) {
  // `clip` "static" (ported from Catch the Cod, 2026-10-28): the symbol's held
  // pose, on its spine above the reel frame — frozen, nothing to update every
  // frame (useLandingAnimations' turbo branch).
  showIdleAnimation(symbolIndex, symbolId, betOverride = null, frozen = false, clip = "idle") {
    if (clip === "static") frozen = true;
    // The Golden Mystery owns this cell — see showWinAnimation.
    if (this.hasGoldenMystery(symbolIndex)) return;

    const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
    if (!overlaySymbol) return;

    const spineKey = `${symbolIndex}_${symbolId}`;
    const spineInstance = this.prePositionedSpines.get(spineKey);
    if (!spineInstance) return;

    const { hasIdle } = this.getAvailableAnimations(symbolIndex, symbolId);
    if (!hasIdle) return;

    // If the same idle is already playing and spine is visible, don't restart.
    // This prevents phase-reset flicker when multiple callers trigger idle on
    // the same symbol in the same frame.
    const existingAnimation = this.activeAnimations.get(symbolIndex);
    if (
      existingAnimation &&
      existingAnimation.symbolId === symbolId &&
      existingAnimation.animationType === "idle" &&
      // The SAME clip — a held `static` is an "idle" entry too, and must not
      // stop the real idle taking over (2026-10-28 review).
      !!existingAnimation.isStatic === (clip === "static") &&
      spineInstance.alpha >= 0.5
    ) {
      // If already showing frozen and caller wants unfrozen, just unfreeze
      if (!frozen && !spineInstance.autoUpdate) {
        spineInstance.autoUpdate = true;
      }
      return;
    }

    // Reset bones: idle does not key every bone, so e.g. a win's scale would
    // carry over. Reset slots only when hidden: when visible (landing -> idle)
    // slot attachments like shadows must be kept. Skipped on first use (the
    // spine is already in setup pose).
    const wasPreviouslyUsed = existingAnimation != null;
    if (wasPreviouslyUsed && spineInstance.skeleton) {
      spineInstance.skeleton.setBonesToSetupPose();
      if (spineInstance.alpha < 0.5) {
        spineInstance.skeleton.setSlotsToSetupPose();
      }
    }

    const idleAnimation = this.selectSpineAnimation(
      spineInstance,
      // "idle",
      clip,
      symbolId,
    );
    if (spineInstance.state && idleAnimation) {
      spineInstance.state.clearTracks();
      spineInstance.state.setAnimation(0, idleAnimation, true);
    }

    // Make visible AFTER animation is queued — prevents stale-pose flash.
    // Force first frame to apply so the skeleton pose matches the new animation
    // before any pixel renders. Without this, setup/stale pose shows for 1 frame.
    spineInstance.state?.update(0);
    spineInstance.state?.apply(spineInstance.skeleton);
    // When frozen (autoUpdate=false), the ticker won't call update(), so mesh
    // vertices would never be recomputed. Force a zero-dt update now so the
    // static pose renders correctly.
    if (frozen) {
      spineInstance.autoUpdate = false;
      spineInstance.update(0);
    }
    spineInstance.alpha = 1;
    spineInstance.visible = true;
    spineInstance.autoUpdate = !frozen;
    overlaySymbol.alpha = 1;
    overlaySymbol.visible = true;

    this.activeAnimations.set(symbolIndex, {
      spineInstance,
      symbolId,
      animationType: "idle",
      // A held `static` stays frozen (unfreezeIdleAnimations skips it).
      isStatic: clip === "static",
    });

    // Show coin value text if this is a coin symbol (pass bet override for recovery)
    this.showCoinValueText(symbolIndex, symbolId, spineInstance, betOverride);
  }

  /**
   * Unfreezes all frozen idle animations on this reel.
   * Called when all reels have stopped to start the idle movement.
   */
  unfreezeIdleAnimations() {
    for (const [, entry] of this.activeAnimations) {
      // if (entry.animationType === "idle" && entry.spineInstance) {
      if (entry.animationType === "idle" && !entry.isStatic && entry.spineInstance) {
        entry.spineInstance.autoUpdate = true;
      }
    }
  }

  /**
   * Switch from the current clip (e.g. win) to idle with a clean reset: clear
   * tracks, setup pose, then idle forced to frame 0 via update(0)+apply() so
   * all symbols start in phase.
   */
  transitionToIdle(symbolIndex, symbolId) {
    // The Golden Mystery owns this cell — see showWinAnimation.
    if (this.hasGoldenMystery(symbolIndex)) return;

    const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
    if (!overlaySymbol) return;

    const spineKey = `${symbolIndex}_${symbolId}`;
    const spineInstance = this.prePositionedSpines.get(spineKey);
    if (!spineInstance) return;

    const { hasIdle } = this.getAvailableAnimations(symbolIndex, symbolId);
    if (!hasIdle) return;

    // Ensure visible and updating
    spineInstance.alpha = 1;
    spineInstance.visible = true;
    spineInstance.autoUpdate = true;
    overlaySymbol.alpha = 1;
    overlaySymbol.visible = true;

    const idleAnimation = this.selectSpineAnimation(
      spineInstance,
      "idle",
      symbolId,
    );
    if (spineInstance.state && idleAnimation) {
      // Full clean reset — clearTracks removes stale animation data that Spine's
      // mix system would otherwise blend from (causing a visible bump).
      spineInstance.state.clearTracks();
      if (spineInstance.skeleton?.setToSetupPose) {
        spineInstance.skeleton.setToSetupPose();
      }
      spineInstance.state.setAnimation(0, idleAnimation, true);
      spineInstance.state.update(0);
      spineInstance.state.apply(spineInstance.skeleton);
    }

    this.activeAnimations.set(symbolIndex, {
      spineInstance,
      symbolId,
      animationType: "idle",
    });
  }

  // Show appear animation (used when symbol first appears; falls back to idle, then landing)
  // @param {number|null} betOverride - Optional bet value for error recovery
  showAppearAnimation(symbolIndex, symbolId, betOverride = null) {
    const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
    if (!overlaySymbol) return;

    const spineKey = `${symbolIndex}_${symbolId}`;
    const spineInstance = this.prePositionedSpines.get(spineKey);
    if (!spineInstance) return;

    const { hasAppear, hasIdle, hasLanding } = this.getAvailableAnimations(
      symbolIndex,
      symbolId,
    );

    // If no animations available, don't show anything
    if (!hasAppear && !hasIdle && !hasLanding) return;

    // Always reset bones (clears stale scale/position from prior animations).
    // Only reset slots when hidden (preserves inherited attachments when visible).
    if (spineInstance.skeleton) {
      spineInstance.skeleton.setBonesToSetupPose();
      if (spineInstance.alpha < 0.5) {
        spineInstance.skeleton.setSlotsToSetupPose();
      }
    }

    let selectedAnimation = null;

    // Try appear first, fallback to idle, then landing
    if (hasAppear) {
      selectedAnimation = this.selectSpineAnimation(
        spineInstance,
        "appear",
        symbolId,
      );
    } else if (hasIdle) {
      selectedAnimation = this.selectSpineAnimation(
        spineInstance,
        "idle",
        symbolId,
      );
    } else if (hasLanding) {
      selectedAnimation = this.selectSpineAnimation(
        spineInstance,
        "land",
        symbolId,
      );
    }

    if (spineInstance.state && selectedAnimation) {
      spineInstance.state.clearTracks();
      spineInstance.state.addAnimation(0, selectedAnimation, true, 0);
    }

    // Make visible AFTER animation is queued
    spineInstance.state?.update(0);
    spineInstance.state?.apply(spineInstance.skeleton);
    spineInstance.alpha = 1;
    spineInstance.visible = true;
    spineInstance.autoUpdate = true;
    overlaySymbol.alpha = 1;
    overlaySymbol.visible = true;

    this.activeAnimations.set(symbolIndex, {
      spineInstance,
      symbolId,
      animationType: "appear",
    });

    // Show coin value text if this is a coin symbol (pass bet override for recovery)
    this.showCoinValueText(symbolIndex, symbolId, spineInstance, betOverride);
  }

  hideWinAnimation(symbolIndex) {
    // Golden Mystery cell: end its celebration by parking it, never by clearing
    // its track 0 (the light chase).
    if (this.hasGoldenMystery(symbolIndex)) {
      // Parked, the reel's identical composite shows through and takes the dim
      // (a live Golden cell draws above it). parkGoldenCell refuses while the
      // feature still owns the cell: parking mid-sequence would hang it.
      this.parkGoldenCell(symbolIndex);
      return;
    }

    const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
    const animationData = this.activeAnimations.get(symbolIndex);
    if (!animationData || !overlaySymbol) return;

    const { spineInstance } = animationData;
    spineInstance.alpha = 0.001;
    spineInstance.visible = false;
    spineInstance.autoUpdate = false;
    overlaySymbol.alpha = 0.001;
    overlaySymbol.visible = false;

    // Hidden, so a full reset is safe: it clears the win's bone transforms
    // (scale, position), which would corrupt the next clip on this spine.
    if (spineInstance.state) {
      spineInstance.state.clearTracks();
    }
    if (spineInstance.skeleton?.setToSetupPose) {
      spineInstance.skeleton.setToSetupPose();
    }

    this.activeAnimations.delete(symbolIndex);

    // Hide coin value text if any
    this.hideCoinValueText(symbolIndex);
  }

  /**
   * Register a landing clip so waitForLandings can wait for it.
   *
   * @param {number} symbolIndex
   * @param {object} entry The spine TrackEntry the clip was queued on. A
   *   non-looping entry fires `complete` once at the end of the clip; a looping
   *   one fires it at the end of each pass, and the FIRST pass is what "landed"
   *   means. Either way one complete is the signal.
   */
  _trackLanding(symbolIndex, entry) {
    if (!entry) return;
    this._pendingLandings.add(symbolIndex);
    entry.listener = {
      complete: () => this._landingSettled(symbolIndex),
      // A landing can also be ENDED without completing — cleared by the next
      // spin, or by a win taking the track. Either way nothing is waiting for
      // it any more, so it must not hold the round.
      end: () => this._landingSettled(symbolIndex),
    };
  }

  /** One landing is done, however it ended. Releases the waiters at zero. */
  _landingSettled(symbolIndex) {
    if (!this._pendingLandings.delete(symbolIndex)) return;
    if (this._pendingLandings.size === 0) this._releaseLandingWaiters();
  }

  _releaseLandingWaiters() {
    if (!this._landingWaiters.length) return;
    const waiters = this._landingWaiters;
    this._landingWaiters = [];
    for (const resolve of waiters) resolve();
  }

  /**
   * Resolves once every landing clip on this reel has finished.
   *
   * Resolves IMMEDIATELY when none is playing — a reel of symbols with no
   * landing clips, a turbo spin with landings suppressed, or a reel that has
   * already settled. So a caller can always await this without checking first.
   *
   * Never rejects and has no timeout of its own: the caller owns the stall
   * guard, because only it knows what to do when a spine dies mid-clip.
   *
   * @returns {Promise<void>}
   */
  waitForLandings() {
    if (this._pendingLandings.size === 0) return Promise.resolve();
    return new Promise((resolve) => this._landingWaiters.push(resolve));
  }

  hideLandingAnimation(symbolIndex) {
    // The Golden Mystery owns this cell — see showWinAnimation.
    if (this.hasGoldenMystery(symbolIndex)) return;

    const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
    const animationData = this.activeAnimations.get(symbolIndex);
    if (!animationData || !overlaySymbol) return;

    if (animationData.animationType !== "landing") return;

    const { spineInstance } = animationData;
    spineInstance.alpha = 0.001;
    spineInstance.visible = false;
    spineInstance.autoUpdate = false;
    overlaySymbol.alpha = 0.001;
    overlaySymbol.visible = false;

    // Spine is invisible (alpha 0.001) — safe to do a full reset.
    if (spineInstance.state) {
      spineInstance.state.clearTracks();
    }
    if (spineInstance.skeleton?.setToSetupPose) {
      spineInstance.skeleton.setToSetupPose();
    }

    this.activeAnimations.delete(symbolIndex);
  }

  hideAllAnimations(exceptPositions = null) {
    // Called at spin start and on cleanup. Landings still registered belong to
    // a finished round: empty the set and release every waiter so nothing stalls
    // the next round's wins (a spine stopped mid-clip never fires `end`).
    this._pendingLandings.clear();
    this._releaseLandingWaiters();

    // First: hide tracked animations (the fast path)
    const activeIndexes = Array.from(this.activeAnimations.keys());
    activeIndexes.forEach((symbolIndex) => {
      if (exceptPositions && exceptPositions.has(symbolIndex)) {
        return;
      }
      this.hideWinAnimation(symbolIndex);
    });

    // Golden cells have no activeAnimations entry (the sequence deletes it to
    // own track 0), so the loop above never reaches them. Park them here,
    // except the rows in exceptPositions.
    for (const symbolIndex of this.goldenControllers?.keys() || []) {
      if (exceptPositions?.has(symbolIndex)) continue;
      // Through parkGoldenCell, never straight at the cell: it holds the gate
      // that keeps a sweep from freezing a sequence still in flight.
      this.parkGoldenCell(symbolIndex);
    }

    // Hide all coin value texts (they should already be hidden by hideWinAnimation, but ensure cleanup)
    // this.hideAllCoinValueTexts();
    // ...EXCEPT on the cells kept above (2026-10-22): a kept coin stays on screen
    // (a trigger coin waiting), and stripping its label left it blank.
    for (const symbolIndex of Array.from(this.coinValueContainers.keys())) {
      if (exceptPositions?.has(symbolIndex)) continue;
      this.hideCoinValueText(symbolIndex);
    }

    // Full sweep: a spine still ticking with no activeAnimations entry (lost in
    // the async win flow) would cost CPU every frame for ever.
    if (!exceptPositions) {
      for (const [key, spine] of this.prePositionedSpines.entries()) {
        if (!spine || !spine.autoUpdate) continue;

        // Cells a reveal holds (_revealHold) are not orphans: the reveal
        // deleted their entry on purpose, and sweeping mid-sequence would lose
        // the `complete` it awaits and hang the round. Not keyed on
        // revealControllers: SC1's controller is never released, so that cell
        // would stay exempt (and ticking) for the whole session.
        const symbolIndex = Number(key.split("_")[0]);
        if (this._revealHold?.has(symbolIndex)) continue;
        // Golden cells tick with no entry too; when to park them is decided by
        // the Golden loop above.
        if (this.goldenControllers?.has(symbolIndex)) continue;

        spine.autoUpdate = false;
        spine.alpha = 0.001;
        spine.visible = false;
        if (spine.state) {
          spine.state.clearTracks();
        }
      }
      this.activeAnimations.clear();
    }
  }

  hideAllAnimationsExcept(upcomingPositions = []) {
    if (!upcomingPositions || upcomingPositions.length === 0) {
      this.hideAllAnimations();
      return;
    }

    // Golden cells are counted too — they hold no activeAnimations entry, so
    // testing that alone would leave a reel of them lit for the next line.
    if (this.activeAnimations.size === 0 && !this.goldenControllers?.size) {
      return;
    }

    const preserveMap = new Map();
    for (let i = 0; i < upcomingPositions.length; i++) {
      const [reelIndex, symbolIndex, symbolId] = upcomingPositions[i];
      if (reelIndex === this.reelIndex) {
        preserveMap.set(symbolIndex, symbolId);
      }
    }

    for (const [symbolIndex, animationData] of this.activeAnimations) {
      const preservedSymbolId = preserveMap.get(symbolIndex);
      if (preservedSymbolId !== animationData.symbolId) {
        this.hideWinAnimation(symbolIndex);
      }
    }

    // Golden cells are kept by ROW only: their sprite always claims the
    // mystery's id, so comparing symbol ids would park all of them every line.
    for (const symbolIndex of this.goldenControllers?.keys() || []) {
      if (preserveMap.has(symbolIndex)) continue;
      this.parkGoldenCell(symbolIndex);
    }
  }

  /**
   * Scale for a symbol spine in a cell: cell height over native height, times
   * the symbol's SYMBOL_SCALE_OVERRIDES entry. Cached per size and symbolPay.
   */
  calculateSpineScale(
    spineInstance,
    containerWidth,
    containerHeight,
    skipAnimationSetup = false,
    symbolId = null,
  ) {
    if (
      !skipAnimationSetup &&
      spineInstance &&
      spineInstance.state &&
      spineInstance.state.data &&
      spineInstance.state.data.skeletonData.animations.length > 0
    ) {
      const defaultAnimation =
        spineInstance.state.data.skeletonData.animations[0];
      spineInstance.state.addAnimation(0, defaultAnimation.name, true, 0);
    }

    // Per-category scale — single lookup with DEFAULT fallback
    const info = symbolId != null ? symbolData[symbolId] : null;
    const symbolPay = info?.symbolPay || '';
    const categoryScale = Settings.SYMBOL_SCALE_OVERRIDES?.[symbolPay]
      ?? Settings.SYMBOL_SCALE_OVERRIDES?.DEFAULT
      ?? 1;

    // Check cache
    const cacheKey = `${containerWidth}_${containerHeight}_${symbolPay}`;
    if (this._spineScaleCache.has(cacheKey)) {
      return this._spineScaleCache.get(cacheKey);
    }

    const nativeWidth = Settings.SYMBOL_WIDTH;
    const nativeHeight = Settings.SYMBOL_HEIGHT;
    const scale = (containerHeight / nativeHeight) * categoryScale;

    const scaledWidth = nativeWidth * scale;
    const scaledHeight = nativeHeight * scale;
    // No centering offset needed: the sprite uses anchor(0.5) at position (0,0),
    // and the spine's origin (0,0) is its design center. Both align at the
    // container origin by default. A non-zero offset here would create a visible
    // shift between the static texture and the overlay spine during transitions.
    const x = 0;
    const y = 0;

    const result = {
      scale,
      nativeWidth,
      nativeHeight,
      x,
      y,
      scaledWidth,
      scaledHeight,
    };

    // Cache result
    this._spineScaleCache.set(cacheKey, result);

    return result;
  }

  createSpineInstance(symbolId) {
    const symbolInfo = symbolData[symbolId];
    if (!symbolInfo?.spine || symbolInfo.spine === "") return null;

    const spineName = symbolInfo.spine;
    const skeletonAlias = `${spineName}-skeleton`;
    const atlasAlias = `${spineName}-atlas`;
    const assets = PIXI.Assets.cache;

    if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) return null;

    try {
      const spine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
        autoUpdate: false,
      });

      // Apply skin if feature is enabled and symbol specifies one (shared spine with multiple skins)
      if (
        FEATURES_CONFIG.SPINE_ANIMATIONS.SHARED_SPINE_SKINS &&
        symbolInfo.skin &&
        spine.skeleton
      ) {
        spine.skeleton.setSkinByName(symbolInfo.skin);
        spine.skeleton.setSlotsToSetupPose();
      }

      return spine;
    } catch {
      return null;
    }
  }

  selectSpineAnimation(spineInstance, animationType, symbolId) {
    const cacheKey = `${symbolId}_${animationType}`;
    let cachedAnimation = this.animationCache.get(cacheKey);
    if (cachedAnimation !== undefined) return cachedAnimation;

    if (!spineInstance.skeleton?.data?.animations) {
      this.animationCache.set(cacheKey, null);
      return null;
    }

    const animations = spineInstance.skeleton.data.animations;
    let selectedAnimation = null;

    if (animationType === "auto") {
      selectedAnimation = animations[0]?.name || null;
    } else {
      const exactMatch = animations.find((anim) => {
        const name = anim.name.toLowerCase();
        const type = animationType.toLowerCase();
        // A "win" request excludes win_fast and win_closed: those are separate
        // clips, only ever chosen by name.
        if (type === "win") {
          return (
            name.includes("win") &&
            !name.includes("win_fast") &&
            !name.includes("win_closed")
          );
        }
        return name.includes(type);
      });
      if (exactMatch) {
        selectedAnimation = exactMatch.name;
      } else if (animationType === "win_closed") {
        // Doors-shut celebration (only the gamble scatter has one); every other
        // symbol falls back to its plain win clip.
        const closedFallback = animations.find((anim) => {
          const n = anim.name.toLowerCase();
          // `win_fast` is excluded for the same reason the plain "win" branch
          // excludes it: a shortened clip is not the pose being asked for.
          return n.includes("win") && !n.includes("win_fast");
        });
        // No animations[0] fallback: a symbol with no win clip (e.g. `my`, whose
        // first clip is a looping `anticipation`) plays nothing.
        selectedAnimation = closedFallback?.name || null;
      } else if (animationType === "win_fast") {
        // win_fast not found → fall back to regular "win"
        const winFallback = animations.find((anim) =>
          anim.name.toLowerCase().includes("win"),
        );
        selectedAnimation = winFallback?.name || animations[0]?.name || null;
      } else if (animationType === "idle") {
        // "static" is the idle-equivalent for spines that don't have an explicit "idle" animation
        // (e.g. jackpot spine j.json: ['land', 'static', 'win'])
        const staticAnim = animations.find((anim) => anim.name === "static");
        selectedAnimation = staticAnim?.name || animations[0]?.name || null;
      } else if (animationType === "land") {
        // Other names animators use for the landing clip ("land" / "landing"
        // match above). "in" is exact-only, or it would match "win".
        const landAlias = animations.find((anim) => {
          const name = anim.name.toLowerCase();
          return name.includes("bounce") || name === "in";
        });
        selectedAnimation = landAlias?.name || animations[0]?.name || null;
      } else {
        selectedAnimation = animations[0]?.name || null;
      }
    }

    this.animationCache.set(cacheKey, selectedAnimation);
    return selectedAnimation;
  }

  _smoothTransitionToAnimation(
    spineInstance,
    newAnimationType,
    symbolId,
    loop = true,
  ) {
    if (!spineInstance || !spineInstance.state) return false;

    const newAnimation = this.selectSpineAnimation(
      spineInstance,
      newAnimationType,
      symbolId,
    );
    if (!newAnimation) return false;

    try {
      const currentTracks = spineInstance.state.tracks;
      const hasCurrentAnimation =
        currentTracks && currentTracks.length > 0 && currentTracks[0];

      if (hasCurrentAnimation) {
        const currentTrack = currentTracks[0];
        const currentAnimationName = currentTrack.animation
          ? currentTrack.animation.name
          : null;

        if (currentAnimationName === newAnimation) return true;
        spineInstance.state.addAnimation(0, newAnimation, loop, 0.1);
      } else {
        spineInstance.state.setAnimation(0, newAnimation, loop);
      }
      return true;
    } catch {
      return false;
    }
  }

  async initializeAllSpineInstances() {
    this.createAllPrePositionedSpines();

    // Force-upload all unique symbol atlas textures to GPU.
    // Deferred to next frame so globalThis.__PIXI_RENDERER__ is available.
    // Only run once from the first reel to avoid redundant uploads.
    if (this.reelIndex === 0) {
      requestAnimationFrame(() => {
        const uniqueSpineNames = [
          ...new Set(
            Object.values(symbolData)
              .map((s) => s.spine)
              .filter((s) => s && s !== "")
          ),
        ];
        SpineAtlasUploader.uploadAll(uniqueSpineNames);
      });
    }
  }

  createAllPrePositionedSpines() {
    const WARMUP_POSITION = 0; // Only warm up in first symbol position
    const WARMUP_REEL = 0; // Only warm up in first reel

    for (
      let symbolIndex = 0;
      symbolIndex < GAME_LAYOUT.SYMBOLS_VISIBLE;
      symbolIndex++
    ) {
      const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
      if (!overlaySymbol) continue;

      for (const symbolId of Object.keys(symbolData).map(Number)) {
        // Conditionally exclude blank symbols for Hold and Win mode
        // BLANK_SYMBOL_ID (0): In base game = wild (w1), in Hold and Win = blank space
        const shouldExcludeBlank =
          FEATURES_CONFIG.HOLD_AND_WIN?.ENABLED && symbolId === BLANK_SYMBOL_ID;

        if (shouldExcludeBlank) {
          continue;
        }
        // H&W grid-only symbols (blank, chests, scarab) never land on the 5x4
        // reels; the H&W grid has its own rigs.
        if (HOLD_AND_WIN_GRID_ONLY_PAYS.has(symbolData[symbolId]?.symbolPay)) {
          continue;
        }

        const symbolInfo = symbolData[symbolId];
        // Early check: skip if symbol doesn't exist or has no valid spine
        if (
          !symbolInfo ||
          !symbolInfo.spine ||
          symbolInfo.spine === null ||
          symbolInfo.spine === "" ||
          typeof symbolInfo.spine !== "string"
        ) {
          continue;
        }

        // Texture-only symbols carry `spine` only so their static texture can be
        // captured; the reel layer owns them. A skeleton here would paint over
        // the cell's real spine.
        if (symbolInfo.textureOnly) {
          continue;
        }

        let spineInstance;

        // Only create and warm up spine in first reel, first position
        if (this.reelIndex === WARMUP_REEL && symbolIndex === WARMUP_POSITION) {
          spineInstance = this.createSpineInstance(symbolId);
          if (!spineInstance) continue;

          const scaleData = this.calculateSpineScale(
            spineInstance,
            this.symbolWidth,
            this.symbolHeight,
            true,
            symbolId,
          );

          spineInstance.position.set(scaleData.x, scaleData.y);
          spineInstance.scale.set(scaleData.scale);
          // The golden frame sits under the mystery lid: equal zIndex draws in
          // insertion order, and the lid (what opens) must be on top.
          spineInstance.zIndex =
            symbolId === GOLDEN_FRAME_SYMBOL_ID ? 9 : 10;
          spineInstance.alpha = 0.001;
          overlaySymbol.addChild(spineInstance);

          // Build the animation availability cache from the warmed-up spine
          const key = `${symbolIndex}_${symbolId}`;
          if (spineInstance.skeleton?.data?.animations?.length > 0) {
            const available = new Set(
              spineInstance.skeleton.data.animations.map((a) =>
                a.name.toLowerCase(),
              ),
            );
            // Only cache if there are actual animations
            if (available.size > 0) {
              this.animationAvailability.set(key, available);
            }
          }

          // Warm up animations (shader compilation + GPU buffer upload happens here)
          // Order matters: each setAnimation replaces the previous animation's meshes.
          // "win" is warmed LAST so its meshes survive in the display list after cleanup.
          this.warmUpSpineAnimation(spineInstance, symbolId, "appear");
          this.warmUpSpineAnimation(spineInstance, symbolId, "idle");
          this.warmUpSpineAnimation(spineInstance, symbolId, "land");
          this.warmUpSpineAnimation(spineInstance, symbolId, "win");

          // Schedule cleanup after GPU has rendered all animation meshes (3 frames)
          this.scheduleWarmupCleanup(spineInstance, symbolIndex);

          const spineKey = `${symbolIndex}_${symbolId}`;
          this.prePositionedSpines.set(spineKey, spineInstance);
        } else {
          // For all other reels and positions, create fresh instances (shaders already compiled)
          spineInstance = this.createSpineInstance(symbolId);
          if (!spineInstance) continue;

          const scaleData = this.calculateSpineScale(
            spineInstance,
            this.symbolWidth,
            this.symbolHeight,
            true,
            symbolId,
          );

          spineInstance.position.set(scaleData.x, scaleData.y);
          spineInstance.scale.set(scaleData.scale);
          // The golden frame sits under the mystery lid: equal zIndex draws in
          // insertion order, and the lid (what opens) must be on top.
          spineInstance.zIndex =
            symbolId === GOLDEN_FRAME_SYMBOL_ID ? 9 : 10;
          spineInstance.alpha = 0.001;
          overlaySymbol.addChild(spineInstance);

          // Animation availability cache. All positions share the symbol's
          // clips, so the entry is keyed on position 0.
          const warmupKey = `${WARMUP_POSITION}_${symbolId}`;

          // Check if we already have the cache entry for this symbol (from position 0)
          let available = this.animationAvailability.get(warmupKey);

          // If not, populate it from this spine instance
          if (
            !available &&
            spineInstance.skeleton?.data?.animations?.length > 0
          ) {
            available = new Set(
              spineInstance.skeleton.data.animations.map((a) =>
                a.name.toLowerCase(),
              ),
            );
            // Only cache if there are actual animations
            if (available.size > 0) {
              this.animationAvailability.set(warmupKey, available);
            }
          }

          // Warm up GPU mesh buffers for all animation types (prevents first-use jitter)
          // "win" is warmed LAST so its meshes survive after cleanup
          this.warmUpSpineAnimation(spineInstance, symbolId, "appear");
          this.warmUpSpineAnimation(spineInstance, symbolId, "idle");
          this.warmUpSpineAnimation(spineInstance, symbolId, "land");
          this.warmUpSpineAnimation(spineInstance, symbolId, "win");
          this.scheduleWarmupCleanup(spineInstance, symbolIndex);

          const spineKey = `${symbolIndex}_${symbolId}`;
          this.prePositionedSpines.set(spineKey, spineInstance);
        }
      }
    }
  }

  warmUpSpineAnimation(spineInstance, symbolId, animationType = "win") {
    if (!spineInstance?.state) return;
    try {
      const animation = this.selectSpineAnimation(
        spineInstance,
        animationType,
        symbolId,
      );
      if (!animation) return;

      // Use setAnimation (replaces current) instead of clearTracks + addAnimation.
      // This preserves the previous animation's mesh data until the new one replaces it,
      // giving the GPU a chance to render each animation's mesh before it's replaced.
      spineInstance.state.setAnimation(0, animation, false);
      spineInstance.state.update(0.016);
      spineInstance.skeleton.updateWorldTransform();

      spineInstance.alpha = 0.001;
      const overlaySymbol = spineInstance.parent;
      if (overlaySymbol) overlaySymbol.alpha = 0.001;
    } catch {}
  }

  /**
   * Finalize warmup cleanup after GPU has had time to render all animation meshes.
   * Called once per spine AFTER all warmUpSpineAnimation calls complete.
   */
  scheduleWarmupCleanup(spineInstance, symbolIndex = null) {
    if (!spineInstance?.state) return;

    const overlaySymbol = spineInstance.parent;

    // Wait 3 frames — ensures PIXI's renderer has drawn the spine mesh
    // and uploaded vertex/texture data to the GPU for all warmed-up animations
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // Skip a cell taken over within these frames (reveal hold or active
          // animation): the overlay mounts with its room, so e.g. a settled
          // gamble card can already be shown on this spine. Same guard as the
          // sweep in hideAllAnimations.
          if (
            symbolIndex !== null &&
            (this._revealHold?.has(symbolIndex) ||
              this.activeAnimations?.has(symbolIndex))
          ) {
            return;
          }

          if (spineInstance.state) {
            // Clear tracks after GPU has rendered all warmed-up meshes (3 frames).
            // Mesh GPU buffers are already cached — clearTracks() just resets track state
            // so the first showWinAnimation() starts from a clean slate.
            spineInstance.state.clearTracks();
            spineInstance.autoUpdate = false;
          }
          spineInstance.alpha = 0.001;
          spineInstance.visible = false;
          if (overlaySymbol) {
            overlaySymbol.alpha = 0.001;
            overlaySymbol.visible = false;
          }
          // Now parked: build its meshes for every clip, a few spines per frame
          // (spineMeshWarm), so a first landing or win does not build them
          // mid-spin. Skipped if the cell is in use by then.
          queueMeshWarm({
            spine: spineInstance,
            isBusy: () =>
              spineInstance.visible ||
              (symbolIndex !== null &&
                (this._revealHold?.has(symbolIndex) ||
                  this.activeAnimations?.has(symbolIndex))),
          });
        });
      });
    });
  }

  forceDeepSpineCleanup() {
    for (const spineInstance of this.prePositionedSpines.values()) {
      if (spineInstance?.state) {
        spineInstance.state.clearTracks();
        spineInstance.autoUpdate = false;
        spineInstance.alpha = 0.001;
        spineInstance.visible = false;
      }
    }

    for (let i = 0; i < this.overlaySymbolRefs.length; i++) {
      const overlaySymbol = this.overlaySymbolRefs[i];
      if (overlaySymbol?.alpha > 0) {
        overlaySymbol.alpha = 0.001;
        overlaySymbol.visible = false;
      }
    }

    this.activeAnimations.clear();
  }

  /**
   * Show appear animation at a specific position (used for cascading expanding wilds)
   * @param {number} symbolIndex - Position index (0-3)
   * @param {number} targetSymbolId - Symbol ID to show (typically wild = 0)
   */
  showAppearAnimationAtPosition(symbolIndex, targetSymbolId) {
    const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
    if (!overlaySymbol) {
      return;
    }

    // Get the pre-positioned spine instance for this position with target symbol
    const spineKey = `${symbolIndex}_${targetSymbolId}`;
    const spineInstance = this.prePositionedSpines.get(spineKey);
    if (!spineInstance) {
      return;
    }

    // Hide any existing animation at this position
    const existingAnimation = this.activeAnimations.get(symbolIndex);
    if (existingAnimation) {
      this.hideWinAnimation(symbolIndex);
    }

    // Make spine and overlay visible
    spineInstance.alpha = 1;
    spineInstance.visible = true;
    spineInstance.autoUpdate = true;
    overlaySymbol.alpha = 1;
    overlaySymbol.visible = true;

    // Play appear animation (fallback to land if appear not available)
    let appearAnimation = this.selectSpineAnimation(
      spineInstance,
      "appear",
      targetSymbolId,
    );

    // If no appear animation found, try land as fallback
    if (
      !appearAnimation ||
      appearAnimation === spineInstance.skeleton?.data?.animations?.[0]?.name
    ) {
      const landAnim = this.selectSpineAnimation(
        spineInstance,
        "land",
        targetSymbolId,
      );
      if (landAnim) appearAnimation = landAnim;
    }

    if (spineInstance.state && appearAnimation) {
      // Bones only: setToSetupPose would also reset slot attachments (DarkSlotMesh leak)
      spineInstance.skeleton.setBonesToSetupPose();
      spineInstance.state.clearTracks();
      // Play appear animation once (no loop) - will stop at last frame when finished
      spineInstance.state.addAnimation(0, appearAnimation, false, 0);
    }

    // Track this as an active animation
    this.activeAnimations.set(symbolIndex, {
      spineInstance,
      symbolId: targetSymbolId,
      animationType: "appear",
    });

    // Show coin value text if this is a coin symbol
    this.showCoinValueText(symbolIndex, targetSymbolId, spineInstance);
  }

  /**
   * Replace visible symbol animations with target symbol appear animation
   * @param {number} targetSymbolId - Symbol ID to replace visible symbols with
   */
  replaceVisibleSymbols(targetSymbolId) {
    // Use GAME_LAYOUT indices to loop through visible positions
    for (const symbolIndex of GAME_LAYOUT.SYMBOL_INDICES) {
      const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
      if (!overlaySymbol) {
        continue;
      }

      // Get the pre-positioned spine instance for this position with target symbol
      const spineKey = `${symbolIndex}_${targetSymbolId}`;
      const spineInstance = this.prePositionedSpines.get(spineKey);
      if (!spineInstance) {
        continue;
      }

      // Hide any existing animation at this position
      const existingAnimation = this.activeAnimations.get(symbolIndex);
      if (existingAnimation) {
        this.hideWinAnimation(symbolIndex);
      }

      // Make spine and overlay visible
      spineInstance.alpha = 1;
      spineInstance.visible = true;
      spineInstance.autoUpdate = true;
      overlaySymbol.alpha = 1;
      overlaySymbol.visible = true;

      // Play appear animation (fallback to land if appear not available)
      let appearAnimation = this.selectSpineAnimation(
        spineInstance,
        "appear",
        targetSymbolId,
      );

      // If no appear animation found, try land as fallback
      if (
        !appearAnimation ||
        appearAnimation === spineInstance.skeleton?.data?.animations?.[0]?.name
      ) {
        const landAnim = this.selectSpineAnimation(
          spineInstance,
          "land",
          targetSymbolId,
        );
        if (landAnim) appearAnimation = landAnim;
      }

      if (spineInstance.state && appearAnimation) {
        spineInstance.state.clearTracks();
        spineInstance.state.addAnimation(0, appearAnimation, true, 0);
      }

      // Track this as an active animation
      this.activeAnimations.set(symbolIndex, {
        spineInstance,
        symbolId: targetSymbolId,
        animationType: "appear",
      });

      // Show coin value text if this is a coin symbol
      this.showCoinValueText(symbolIndex, targetSymbolId, spineInstance);
    }
  }

  /**
   * Create expanding wild spine instance
   */
  createExpandingWildSpine() {
    try {
      if (!this.expandingWildSpineData) {
        return null;
      }

      const assets = PIXI.Assets.cache;
      const skeletonKey = this.expandingWildSpineData.skeleton;
      const atlasKey = this.expandingWildSpineData.atlas;

      if (!assets.has(skeletonKey) || !assets.has(atlasKey)) {
        return null;
      }

      const expandingWildSpine = Spine.from({
        skeleton: skeletonKey,
        atlas: atlasKey,
      });

      if (
        !expandingWildSpine ||
        !expandingWildSpine.skeleton ||
        !expandingWildSpine.state
      ) {
        return null;
      }

      if (
        !expandingWildSpine.skeleton.data ||
        !expandingWildSpine.skeleton.data.animations
      ) {
        return null;
      }

      return expandingWildSpine;
    } catch (error) {
      return null;
    }
  }

  /**
   * Scale and centring offsets that fit the expanding wild into a box (the full
   * reel), keeping its aspect ratio. Cached per box size.
   * @param {Spine} spineInstance - The spine instance to scale
   * @param {number} containerWidth - Available width
   * @param {number} containerHeight - Available height (full reel height)
   * @param {boolean} preserveAnimation - Do not set the first clip for bounds
   * @returns {Object} - Scale and positioning information
   */
  calculateExpandingWildScale(
    spineInstance,
    containerWidth,
    containerHeight,
    preserveAnimation = false,
  ) {
    // Set default animation to get proper bounds (only if not preserving current animation)
    if (
      !preserveAnimation &&
      spineInstance &&
      spineInstance.state &&
      spineInstance.state.data &&
      spineInstance.state.data.skeletonData.animations.length > 0
    ) {
      const defaultAnimation =
        spineInstance.state.data.skeletonData.animations[0];
      spineInstance.state.setAnimation(0, defaultAnimation.name, true);
    }

    // Check cache first
    const cacheKey = `${containerWidth}_${containerHeight}`;
    if (
      this._expandingWildScaleCache &&
      this._expandingWildScaleCache.cacheKey === cacheKey
    ) {
      return this._expandingWildScaleCache.result;
    }

    // Calculate scale to fit within container while maintaining aspect ratio
    const nativeWidth = Settings.SYMBOL_WIDTH;
    const nativeHeight = Settings.SYMBOL_HEIGHT;

    const widthScale = containerWidth / nativeWidth;
    const heightScale = containerHeight / nativeHeight;
    const scale = Math.min(widthScale, heightScale);

    // Calculate final dimensions and centering offsets
    const scaledWidth = nativeWidth * scale;
    const scaledHeight = nativeHeight * scale;
    const x = (containerWidth - scaledWidth) / 2;
    const y = (containerHeight - scaledHeight) / 2;

    const result = {
      scale,
      nativeWidth,
      nativeHeight,
      x,
      y,
      scaledWidth,
      scaledHeight,
    };

    // Cache result
    this._expandingWildScaleCache = {
      cacheKey,
      result,
    };

    return result;
  }

  /**
   * Apply scaling and positioning to expanding wild spine
   */
  applyExpandingWildScaling() {
    if (!this.expandingWildSpine || this.expandingWildSpine.destroyed) {
      return;
    }

    // Get current animation before scaling (to preserve it)
    let currentAnimation = null;
    let currentLoop = false;
    if (this.expandingWildSpine.state) {
      const currentTrack = this.expandingWildSpine.state.getCurrent(0);
      if (currentTrack && currentTrack.animation) {
        currentAnimation = currentTrack.animation.name;
        currentLoop = currentTrack.loop;
      }
    }

    // Calculate full reel height (4 visible symbols)
    const fullReelHeight = this.symbolHeight * GAME_LAYOUT.SYMBOLS_VISIBLE;

    const scaleData = this.calculateExpandingWildScale(
      this.expandingWildSpine,
      this.symbolWidth,
      fullReelHeight,
      true, // Keep the current animation
    );

    if (this.expandingWildSpine.scale) {
      this.expandingWildSpine.scale.set(
        scaleData.scale,
        scaleData.scale * this.expandingWildHeightMultiplier,
      );
    }

    if (this.expandingWildSpine.position) {
      const yOffset = fullReelHeight * this.expandingWildYOffsetMultiplier;
      this.expandingWildSpine.position.set(scaleData.x, scaleData.y + yOffset);
    }

    // Restore the animation that was playing before scaling
    if (currentAnimation && this.expandingWildSpine.state) {
      try {
        this.expandingWildSpine.state.setAnimation(
          0,
          currentAnimation,
          currentLoop,
        );
      } catch (error) {
        // Silent fail - animation might not exist
      }
    }
  }

  /**
   * Warm up expanding wild spine with all animations (appear, idle, win)
   * Same pattern as warmUpSpineAnimation for symbol spines
   */
  warmUpExpandingWildSpine() {
    if (!this.expandingWildSpine || !this.expandingWildSpine.state) {
      return;
    }

    const animations = ["appear", "idle", "win"];

    animations.forEach((animName) => {
      try {
        // Check if animation exists
        const hasAnim =
          this.expandingWildSpine.skeleton?.data?.animations?.find(
            (anim) => anim.name === animName,
          );

        if (!hasAnim) {
          return;
        }

        // Set animation
        this.expandingWildSpine.state.clearTracks();
        this.expandingWildSpine.state.setAnimation(0, animName, false);

        // Update one frame to trigger shader compilation
        this.expandingWildSpine.state.update(0.016);

        // Populate bone/slot hierarchy for GPU
        try {
          if (this.expandingWildSpine.skeleton.physics !== undefined) {
            this.expandingWildSpine.skeleton.updateWorldTransform();
          }
        } catch (transformError) {
          // Skip if physics not initialized
        }

        // Keep at alpha = 0.001 so PixiJS continues rendering it
        this.expandingWildSpine.alpha = 0.001;

        // Clear tracks after warmup frame renders
        requestAnimationFrame(() => {
          if (this.expandingWildSpine?.state) {
            this.expandingWildSpine.state.clearTracks();
            this.expandingWildSpine.alpha = 0.001;
          }
        });
      } catch (error) {
        // Silent fail - warmup is optional optimization
      }
    });
  }

  /**
   * Get available animation names for expanding wild spine
   */
  getExpandingWildAvailableAnimations() {
    if (!this.expandingWildSpine?.skeleton?.data?.animations) {
      return [];
    }
    return this.expandingWildSpine.skeleton.data.animations.map(
      (anim) => anim.name,
    );
  }

  /**
   * Initialize expanding wild spine and add to container
   */
  initializeExpandingWildSpine() {
    if (this.expandingWildInitialized || !this.reelContainer) {
      return;
    }

    this.expandingWildSpine = this.createExpandingWildSpine();

    if (!this.expandingWildSpine) {
      return;
    }

    // Validate spine components
    const missingComponents = [];
    if (!this.expandingWildSpine.skeleton) missingComponents.push("skeleton");
    if (!this.expandingWildSpine.state) missingComponents.push("state");
    if (!this.expandingWildSpine.skeleton?.data)
      missingComponents.push("skeleton.data");
    if (!this.expandingWildSpine.skeleton?.slots)
      missingComponents.push("skeleton.slots");
    if (!this.expandingWildSpine.skeleton?.bones)
      missingComponents.push("skeleton.bones");

    if (missingComponents.length > 0) {
      return;
    }

    // Apply x2 skin for middle reel (index 2) - only if multiplier feature is enabled
    if (
      this.reelIndex === 2 &&
      this.expandingWildSpine.skeleton &&
      FEATURES_CONFIG.EXPANDING_WILDS.MULTIPLIER_ENABLED
    ) {
      try {
        const x2Skin = this.expandingWildSpine.skeleton.data.findSkin("x2");
        if (x2Skin) {
          this.expandingWildSpine.skeleton.setSkin(x2Skin);
          this.expandingWildSpine.skeleton.setSlotsToSetupPose();
        }
      } catch (error) {
        // Fail silently
      }
    }

    this.expandingWildSpine.position.set(0, 0);
    this.expandingWildSpine.zIndex = 15; // Higher than symbol animations
    this.expandingWildSpine.alpha = 0.001; // Hidden initially, keep in render pipeline
    this.isExpandingWildVisible = false;

    this.reelContainer.addChild(this.expandingWildSpine);
    this.applyExpandingWildScaling();

    if (this.expandingWildSpine.parent !== this.reelContainer) {
      return;
    }

    // Warm its clips (shader compilation + GPU upload) before first use
    this.warmUpExpandingWildSpine();

    this.expandingWildInitialized = true;
  }

  /**
   * Clear all active timeouts for expanding wild animations
   */
  clearExpandingWildTimeouts() {
    this.expandingWildTimeouts.forEach(clearTimeout);
    this.expandingWildTimeouts = [];
  }

  /**
   * Hide expanding wild animation immediately (synchronous, same as regular win animations)
   */
  hideExpandingWildFullReel() {
    if (!this.expandingWildSpine || this.expandingWildSpine.destroyed) {
      this.isExpandingWildVisible = false;
      this.currentExpandingWildAnimation = null;
      return;
    }

    // Synchronous cleanup - same pattern as hideWinAnimation()
    this.expandingWildSpine.alpha = 0.001; // Keep in render pipeline
    this.expandingWildSpine.visible = false;
    this.expandingWildSpine.autoUpdate = false;
    this.isExpandingWildVisible = false;
    this.currentExpandingWildAnimation = null;

    if (this.expandingWildSpine.state) {
      try {
        this.expandingWildSpine.state.clearTracks();
        // Bones only: setToSetupPose would also reset slot attachments (DarkSlotMesh leak)
        this.expandingWildSpine.skeleton.setBonesToSetupPose();
      } catch (error) {
        // Silent fail
      }
    }
  }

  /**
   * Force stop expanding wild animations (synchronous, same as regular win animations)
   */
  forceStopExpandingWildFullReel() {
    this.clearExpandingWildTimeouts();
    // Direct synchronous cleanup - same pattern as regular animations
    this.hideExpandingWildFullReel();
  }

  /**
   * Play expanding wild full-reel animation (appear animation, then idle)
   */
  playExpandingWildFullReel() {
    // Lazy initialization if not already initialized
    if (!this.expandingWildInitialized) {
      this.initializeExpandingWildSpine();
    }

    if (
      !this.expandingWildSpine ||
      this.expandingWildSpine.destroyed ||
      !this.expandingWildSpine.state
    ) {
      return;
    }

    requestAnimationFrame(() => {
      if (
        !this.expandingWildSpine ||
        this.expandingWildSpine.destroyed ||
        !this.expandingWildSpine.state
      ) {
        return;
      }

      try {
        this.expandingWildSpine.state.clearTracks();
        this.expandingWildSpine.state.clearListeners();
        this.expandingWildSpine.state.apply(this.expandingWildSpine.skeleton);
        // Bones only: setToSetupPose would also reset slot attachments (DarkSlotMesh leak)
        this.expandingWildSpine.skeleton.setBonesToSetupPose();

        this.expandingWildSpine.alpha = 1; // Show
        this.expandingWildSpine.visible = true;
        this.expandingWildSpine.autoUpdate = true;
        this.isExpandingWildVisible = true;

        // Sound is played centrally in useWinController
        // Middle reel (x2 multiplier) plays additional sound when entering idle

        // Play "appear" animation (non-looping), then transition to "idle" (looping)
        this.expandingWildSpine.state.setAnimation(0, "appear", false);
        this.expandingWildSpine.state.addAnimation(0, "idle", true, 0);
        this.currentExpandingWildAnimation = "appear";

        // Add listener for when "appear" completes and "idle" starts
        // Play multiplier sound for middle reel (reel 2 with x2 skin) - only if multiplier feature is enabled
        if (
          this.reelIndex === 2 &&
          FEATURES_CONFIG.EXPANDING_WILDS.MULTIPLIER_ENABLED
        ) {
          const completeListener = {
            complete: (entry) => {
              if (entry.animation.name === "appear") {
                soundManager.play(SOUND_NAME.expanding_wild_multiplier);

                // Remove listener after playing once
                this.expandingWildSpine.state.removeListener(completeListener);
              }
            },
          };
          this.expandingWildSpine.state.addListener(completeListener);
        }
      } catch (error) {
        // Silent fail
        this.hideExpandingWildFullReel();
      }
    });
  }

  /**
   * Play expanding wild win animation (switches from idle to win)
   */
  playExpandingWildFullReelWin() {
    if (
      !this.expandingWildSpine ||
      this.expandingWildSpine.destroyed ||
      !this.expandingWildSpine.state ||
      !this.isExpandingWildVisible
    ) {
      return;
    }

    this._pendingRAF = requestAnimationFrame(() => {
      this._pendingRAF = null;
      if (
        !this.expandingWildSpine ||
        this.expandingWildSpine.destroyed ||
        !this.expandingWildSpine.state
      ) {
        return;
      }

      try {
        // Switch to win animation (looping)
        this.expandingWildSpine.state.setAnimation(0, "win", true);
        this.currentExpandingWildAnimation = "win";
      } catch (error) {
        // Silent fail
      }
    });
  }

  /**
   * Play dark animation for expanding wild (indicates it's not part of current line)
   */
  playExpandingWildDarkAnimation() {
    if (
      !this.expandingWildSpine ||
      this.expandingWildSpine.destroyed ||
      !this.expandingWildSpine.state ||
      !this.isExpandingWildVisible
    ) {
      return;
    }

    try {
      // Switch to dark animation (play once, hold at last frame)
      this.expandingWildSpine.state.setAnimation(0, "dark", false);
      this.currentExpandingWildAnimation = "dark";
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * Play win animation for expanding wild (restore normal appearance)
   */
  playExpandingWildWinAnimation() {
    if (
      !this.expandingWildSpine ||
      this.expandingWildSpine.destroyed ||
      !this.expandingWildSpine.state ||
      !this.isExpandingWildVisible
    ) {
      return;
    }

    try {
      // Switch to win animation (looping)
      this.expandingWildSpine.state.setAnimation(0, "win", true);
      this.currentExpandingWildAnimation = "win";
    } catch (error) {
      // Silent fail
    }
  }

  cleanup() {
    // Cancel any pending requestAnimationFrame
    if (this._pendingRAF) {
      cancelAnimationFrame(this._pendingRAF);
      this._pendingRAF = null;
    }

    this.hideAllAnimations();
    this.hideExpandingWildFullReel();
    this.clearExpandingWildTimeouts();

    // Cleanup all coin value texts
    this.hideAllCoinValueTexts();

    for (const spineInstance of this.prePositionedSpines.values()) {
      if (spineInstance) {
        if (spineInstance.state) spineInstance.state.clearTracks();
        if (spineInstance.parent) {
          spineInstance.parent.removeChild(spineInstance);
        }
        spineInstance.destroy({
          children: true,
          texture: false,
          baseTexture: false,
        });
      }
    }

    // Cleanup expanding wild spine
    if (this.expandingWildSpine && !this.expandingWildSpine.destroyed) {
      try {
        if (this.expandingWildSpine.state) {
          this.expandingWildSpine.state.clearTracks();
          this.expandingWildSpine.state.clearListeners();
        }

        if (this.expandingWildSpine.parent) {
          this.expandingWildSpine.parent.removeChild(this.expandingWildSpine);
        }

        this.expandingWildSpine.destroy({
          children: true,
          texture: false,
          baseTexture: false,
        });
      } catch (error) {
        // Silent fail
      }
    }

    this.prePositionedSpines.clear();
    this.activeAnimations.clear();
    this.animationCache.clear();
    this.animationAvailability.clear();
    this.coinValueContainers.clear();
    this.expandingWildSpine = null;
    this.expandingWildInitialized = false;
    this.isExpandingWildVisible = false;
    this.currentExpandingWildAnimation = null;
    this.destroyRevealSymbols();
  }

  // ── Reveal symbols ────────────────────────────────────────────────────────
  //
  // Symbols that land, open, spin a strip of other symbols inside themselves
  // and settle on a result. The sequence lives in RevealSymbolController; this
  // file only looks the symbol up (REVEAL_SYMBOLS) and hands off.

  /** True when this symbol runs a reveal sequence instead of a plain clip. */
  hasRevealSequence(symbolId) {
    return !!REVEAL_SYMBOLS[symbolId];
  }

  /**
   * Run a symbol's full reveal sequence and resolve once it has settled.
   *
   * @param {number} symbolIndex Row on the reel.
   * @param {number} symbolId
   * @param {*}      result      Item the inner strip must land on.
   * @param {Object|null} configOverride Merged over the built config for this round.
   * @returns {Promise<boolean>} whether the sequence actually ran (callers such
   *          as the `+FS` walk cannot tell a guard return from the promise alone).
   */
  async playRevealSequence(symbolIndex, symbolId, result, configOverride = null) {
    const entry = REVEAL_SYMBOLS[symbolId];
    if (!entry) return false;

    // The host may not be the symbol: a `+FS` scatter reveals inside SC1's
    // window, so the pooled spine (and the golden hosting below) use the host's
    // id.
    const hostSymbolId = entry.hostSymbolId ?? symbolId;

    const spineInstance = this.prePositionedSpines.get(
      `${symbolIndex}_${hostSymbolId}`,
    );
    if (!spineInstance) return false;

    // Take this cell over. Any clip the win display started must not fight the
    // sequence for track 0, and the overlay must stay visible throughout.
    this.activeAnimations.delete(symbolIndex);

    // Appear in the setup pose (door shut, matching the static texture below)
    // so a stale pose from the previous round never flashes. Matters in turbo,
    // where landing is skipped and nothing else has reset this pooled spine.
    spineInstance.state?.clearTracks();
    spineInstance.skeleton?.setToSetupPose?.();
    try {
      spineInstance.skeleton?.updateWorldTransform(
        spineInstance.skeleton.physics !== undefined ? 2 : 0,
      );
    } catch {
      try {
        spineInstance.skeleton?.updateWorldTransform();
      } catch {}
    }

    // On a Golden cell the reveal plays inside the frame's window, not as a
    // sibling covering the whole frame. releaseRevealSymbol puts it back.
    this._hostRevealInGolden(symbolIndex, spineInstance, hostSymbolId);

    spineInstance.alpha = 1;
    spineInstance.visible = true;
    // Wake it: parked pooled spines have autoUpdate false, and in turbo no
    // landing has woken it, so `anticipation` would never complete and the
    // sequence would hang holding criticalAnimationPlaying.
    spineInstance.autoUpdate = true;
    const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
    if (overlaySymbol) {
      overlaySymbol.alpha = 1;
      overlaySymbol.visible = true;
    }

    if (!this.revealControllers) this.revealControllers = new Map();
    this.revealControllers.get(symbolIndex)?.destroy();

    const controller = new RevealSymbolController({
      spine: spineInstance,
      // configOverride varies one round without a second config, e.g. the
      // mystery's anticipation length from what the round pays (mysteryDrama).
      config: {
        ...entry.buildConfig(this.symbolWidth, this.symbolHeight),
        ...(configOverride || {}),
      },
      cellWidth: this.symbolWidth,
      cellHeight: this.symbolHeight,
    });
    this.revealControllers.set(symbolIndex, controller);

    // Held only while the sequence is actually running — see the sweep in
    // hideAllAnimations for why the window has to be this narrow.
    if (!this._revealHold) this._revealHold = new Set();
    this._revealHold.add(symbolIndex);
    try {
      await controller.play(entry.toStripItem(result));
    } finally {
      this._revealHold.delete(symbolIndex);
    }
    return true;
  }

  /**
   * Spin an already-open reveal again, reusing its strip (the window stays
   * open). For the gamble scatter inside a Golden Mystery that landed blank:
   * the collector chains a respin and the blank re-rolls. See
   * RevealSymbolController.respin.
   *
   * @param {number} symbolIndex Row on the reel.
   * @param {number} symbolId
   * @param {*}      result      What it lands on this time.
   * @param {Object} spin        Respin options, passed through.
   * @returns {Promise<boolean>} false when this cell has no live reveal.
   */
  async respinRevealSequence(symbolIndex, symbolId, result, spin = {}) {
    const controller = this.revealControllers?.get(symbolIndex);
    if (!controller) return false;
    const entry = REVEAL_SYMBOLS[symbolId];
    if (!entry) return false;

    // Held for the same reason play does: the sweep in hideAllAnimations must
    // not take this cell while its strip is turning.
    if (!this._revealHold) this._revealHold = new Set();
    this._revealHold.add(symbolIndex);
    try {
      // One strip per cell: only playRevealSequence builds one, and it destroys
      // the previous controller first.
      return await controller.respin(entry.toStripItem(result), spin);
    } finally {
      this._revealHold.delete(symbolIndex);
    }
  }

  /**
   * Make a landed reveal symbol celebrate. The win controller calls this when
   * it starts the rest of the trigger line, so the line celebrates together.
   *
   * @param {number} symbolIndex Row on the reel.
   * @returns {boolean} false when this cell has no live reveal.
   */
  playRevealWin(symbolIndex) {
    return this.revealControllers?.get(symbolIndex)?.playWin() ?? false;
  }

  /**
   * The static reel texture a settled reveal must leave behind, if any.
   *
   * A reveal runs up here on the overlay, while the reel's own texture below
   * it is still whatever landed. When the two disagree — a blank result over
   * an unopened `scg` — restoring the statics shows the wrong symbol. The
   * config decides what the cell should hold; the CALLER owns the reel and
   * performs the swap, because this class has no reel controller.
   *
   * @param   {number} symbolId The host symbol that ran the reveal.
   * @param   {*}      result   Round payload, same value playRevealSequence got.
   * @returns {{symbolId:number,label:Object|null}|null} what the cell must
   *          hold, or null to leave it alone.
   */
  getRevealStaticCell(symbolId, result) {
    const entry = REVEAL_SYMBOLS[symbolId];
    if (!entry?.toStaticCell) return null;
    return entry.toStaticCell(entry.toStripItem?.(result) ?? result);
  }

  /**
   * Put a pooled spine back in its setup pose before it is shown again.
   *
   * Needed after a reveal: the reveal deletes the cell's activeAnimations entry,
   * so showIdleAnimation treats the spine as first-used and skips its reset,
   * and the previous round's win pose (scaled bones, slot attachments idle does
   * not key) would flash for a frame.
   *
   * @param {number} symbolIndex Row on the reel.
   * @param {number} symbolId    The symbol whose pooled spine is about to show.
   */
  resetSymbolPose(symbolIndex, symbolId) {
    // The Golden Mystery owns this cell — see showWinAnimation.
    if (this.hasGoldenMystery(symbolIndex)) return;

    const spineInstance = this.prePositionedSpines.get(
      `${symbolIndex}_${symbolId}`,
    );
    if (!spineInstance?.skeleton) return;

    spineInstance.state?.clearTracks();
    spineInstance.skeleton.setBonesToSetupPose();
    spineInstance.skeleton.setSlotsToSetupPose();
  }

  /**
   * Move a reveal's pooled spine into the Golden Mystery's window, if this row
   * is running one, remembering where it came from so it can be put back.
   * Position is zeroed (the window's origin is the cell centre); scale is kept,
   * because the frame's `fit` cancels the bone chain.
   *
   * @param {number} symbolIndex Row on the reel.
   * @param {object} spineInstance The pooled spine about to play.
   * @param {number} symbolId Which pooled spine this is, so teardown hands back
   *        the right one.
   */
  _hostRevealInGolden(symbolIndex, spineInstance, symbolId) {
    const host = this.goldenControllers?.get(symbolIndex)?.revealHost?.();
    if (!host || spineInstance.parent === host) return;
    if (!this._revealHosted) this._revealHosted = new Map();
    // Only the FIRST host is remembered. A second sequence on the same cell
    // must go back to the container, never to the window it is standing in.
    if (!this._revealHosted.has(symbolIndex)) {
      this._revealHosted.set(symbolIndex, {
        parent: spineInstance.parent,
        x: spineInstance.x,
        y: spineInstance.y,
        // Kept so clearGoldenMysteries can hand the reveal back BEFORE it
        // destroys the frame (the pool is keyed by symbol id); otherwise the
        // pooled scg spine is destroyed with the window.
        symbolId,
      });
    }
    spineInstance.x = 0;
    spineInstance.y = 0;
    host.addChild(spineInstance);
  }

  /**
   * Put a hosted reveal spine back where the pool left it.
   *
   * Called on the way out, and unconditionally: the golden frame it was living
   * in is destroyed with its controller, so a spine left parented to it would
   * go down with it and the pool would hand out a dead object next round.
   *
   * @param {number} symbolIndex Row on the reel.
   * @param {object} spineInstance The pooled spine being handed back.
   */
  _unhostReveal(symbolIndex, spineInstance) {
    const home = this._revealHosted?.get(symbolIndex);
    if (!home) return;
    this._revealHosted.delete(symbolIndex);
    spineInstance.x = home.x;
    spineInstance.y = home.y;
    home.parent?.addChild(spineInstance);
  }

  /**
   * Take a settled reveal off a cell and return its host spine to the pool:
   * no tracks, setup pose, hidden, asleep. The caller has already given the
   * cell its real symbol, so it is an ordinary cell for the win display again.
   *
   * Only for reveals that leave nothing behind. SC1 must NOT be released: its
   * open window with the awarded package IS the settled cell.
   *
   * @param {number} symbolIndex Row on the reel.
   * @param {number} symbolId    The host symbol that ran the reveal.
   */
  releaseRevealSymbol(symbolIndex, symbolId) {
    const controller = this.revealControllers?.get(symbolIndex);
    if (controller) {
      controller.destroy();
      this.revealControllers.delete(symbolIndex);
    }

    const spineInstance = this.prePositionedSpines.get(
      `${symbolIndex}_${symbolId}`,
    );
    if (!spineInstance) return;

    // Home first: the window it may be standing in belongs to a Golden cell
    // whose controller is destroyed on the same call as this one.
    this._unhostReveal(symbolIndex, spineInstance);

    // Hidden BEFORE the pose reset: the setup pose is fully opaque (frame,
    // doors, lights) and must never show on the board.
    spineInstance.alpha = 0.001;
    spineInstance.visible = false;
    // Back to sleep (playRevealSequence woke it), or it ticks for nothing.
    spineInstance.autoUpdate = false;
    spineInstance.state?.clearTracks();
    spineInstance.skeleton?.setBonesToSetupPose();
    spineInstance.skeleton?.setSlotsToSetupPose();
  }

  // ── Golden Mystery (B2) ───────────────────────────────────────────────────
  //
  // A Mystery that reveals into B2 turns its cell into a respin. Per cell:
  //   lid    pooled mystery spine (MYSTERY_SYMBOL_ID): build-up, doors, fade
  //   frame  pooled golden frame (GOLDEN_FRAME_SYMBOL_ID): lights and window
  //   golden layer and track, built by GoldenMysteryCellController in the window
  //
  // Held in goldenControllers, not revealControllers: it is not a reveal that
  // settles and must not be reached by code walking the reveal controllers.
  // Torn down at spin start by clearGoldenMysteries. Dormant in this game: both
  // ids are null (see SymbolData).

  /**
   * Run one Golden cell: build-up, doors, lid, track, landing.
   *
   * @param   {number} symbolIndex Row on the reel.
   * @param   {Object} p
   * @param   {{symbolId:number,value:number}} p.outcome What the server landed.
   * @param   {Array}  p.items     The pool the track spins.
   * @param   {string} p.anticipationClip
   * @returns {Promise<void>}
   */
  async playGoldenMysterySequence(
    symbolIndex,
    { outcome, items, anticipationClip, stopOrder, onRevealed } = {},
  ) {
    // Only the frame comes from the pool here; the golden layer is built by the
    // controller and injected into the window's `attachment_slot_symbol`. The
    // frame is GOLDEN_FRAME_SYMBOL_ID, not the landed mystery, which is a lid
    // with doors only.
    const frameSpine = this.prePositionedSpines.get(
      `${symbolIndex}_${GOLDEN_FRAME_SYMBOL_ID}`,
    );
    if (!frameSpine) {
      console.warn(
        "[GoldenMystery] No pooled mystery spine on reel",
        this.reelIndex,
        "row",
        symbolIndex,
      );
      return;
    }

    // Take the cell over from the win display and start from a known pose, as
    // playRevealSequence does (turbo skips landing, so nothing has reset it).
    this.activeAnimations.delete(symbolIndex);
    frameSpine.state?.clearTracks();
    frameSpine.skeleton?.setToSetupPose?.();
    try {
      frameSpine.skeleton?.updateWorldTransform(
        frameSpine.skeleton.physics !== undefined ? 2 : 0,
      );
    } catch {
      try {
        frameSpine.skeleton?.updateWorldTransform();
      } catch {}
    }
    frameSpine.autoUpdate = true;
    frameSpine.alpha = 1;
    frameSpine.visible = true;
    const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
    if (overlaySymbol) {
      overlaySymbol.alpha = 1;
      overlaySymbol.visible = true;
    }

    if (!this.goldenControllers) this.goldenControllers = new Map();
    this.goldenControllers.get(symbolIndex)?.destroy();

    // The lid: the ordinary mystery the player landed, above the frame. It
    // plays the build-up and doors, then fades to uncover the frame. When it is
    // missing (null) the controller has the frame open itself.
    const lidSpine =
      this.prePositionedSpines.get(`${symbolIndex}_${MYSTERY_SYMBOL_ID}`) || null;
    if (lidSpine) {
      lidSpine.state?.clearTracks();
      lidSpine.skeleton?.setToSetupPose?.();
      lidSpine.autoUpdate = true;
      lidSpine.alpha = 1;
      lidSpine.visible = true;
    }

    const controller = new GoldenMysteryCellController({
      frameSpine,
      lidSpine,
      makeGolden: buildGoldenMysterySpine,
      // For the moments a flat cell cannot act: a multiplier firing, a collector
      // gathering, an upgrade bursting. See playCellSpine.
      makeFeatureSpine: buildFeatureSpine,
      // The cell's own overlay container, purely so the controller can find the
      // flat layer above it — the one place a multiplier's trail can cross the
      // board. See GoldenMysteryCellController.trailLayer.
      cellContainer: overlaySymbol,
      config: buildGoldenMysteryConfig(),
      cellWidth: this.symbolWidth,
      cellHeight: this.symbolHeight,
    });
    this.goldenControllers.set(symbolIndex, controller);

    // Held in _revealHold for the WHOLE feature (until spin start), not just the
    // sequence: the lid is held open by a pinned track entry, and the orphan
    // sweep clearing it would snap the golden sign back over the track.
    if (!this._revealHold) this._revealHold = new Set();
    this._revealHold.add(symbolIndex);

    await controller.play({
      outcome,
      items,
      anticipationClip,
      stopOrder,
      onRevealed,
    });
  }

  /**
   * The Golden cell on this row, for the loop to act on.
   *
   * @param   {number} symbolIndex
   * @returns {GoldenMysteryCellController|null}
   */
  goldenCell(symbolIndex) {
    return this.goldenControllers?.get(symbolIndex) ?? null;
  }

  /**
   * True while this row belongs to the Golden Mystery: from the sequence start
   * until clearGoldenMysteries at spin start, settled and parked cells included.
   *
   * Every ordinary presentation call for the row is guarded on this: the cell's
   * sprite still claims the mystery's id (the board was masked), so resolving a
   * spine from it would land on the mystery and shut its doors.
   */
  hasGoldenMystery(symbolIndex) {
    return !!this.goldenControllers?.has(symbolIndex);
  }

  /**
   * Put one Golden cell to sleep mid-round without giving it up.
   *
   * A live Golden cell draws above the dim, so cells the current line is not
   * lighting are parked and the reel's identical composite stands in and dims
   * (see setCellVisible). Parked, not destroyed, because which cells pay
   * changes per line and a later line may wake it. clearGoldenMysteries hands
   * every cell back at spin start.
   *
   * @param {number} symbolIndex Row on the reel.
   */
  parkGoldenCell(symbolIndex) {
    const controller = this.goldenControllers?.get(symbolIndex);
    if (!controller) return;
    // Never mid-sequence: parking stops the spine ticking, so the `complete` the
    // cell awaits would never arrive and the feature would hang. Only cells the
    // feature has settled (settleGoldenCell) can be parked.
    if (!this._goldenSettled?.has(symbolIndex)) return;
    controller.stopWin?.();
    controller.setCellVisible?.(false);
  }

  /**
   * The feature is done with this cell: from now on the win cycle owns it.
   *
   * Called once per Golden cell, from the settle that paints its flat
   * composite. Before this, nothing may park the cell — see parkGoldenCell.
   *
   * @param {number}  symbolIndex Row on the reel.
   * @param {boolean} [park] Park it immediately. True for a cell no win is
   *        lighting, so the board reads correctly the moment the feature ends
   *        rather than a line-change later.
   */
  settleGoldenCell(symbolIndex, park = false) {
    if (!this.goldenControllers?.has(symbolIndex)) return;
    if (!this._goldenSettled) this._goldenSettled = new Set();
    this._goldenSettled.add(symbolIndex);
    if (park) this.parkGoldenCell(symbolIndex);
  }

  /** Give every Golden cell back. Called at spin start. */
  clearGoldenMysteries() {
    if (!this.goldenControllers?.size) return;
    for (const [symbolIndex, controller] of this.goldenControllers) {
      // Hand the hosted reveal back FIRST: it lives in the frame's window, which
      // destroy() takes down with its children, and the pooled scg spine would
      // die with it (see _hostRevealInGolden).
      const hostedId = this._revealHosted?.get(symbolIndex)?.symbolId;
      if (hostedId !== undefined && hostedId !== null) {
        this.releaseRevealSymbol(symbolIndex, hostedId);
      }
      controller.destroy();
      this._revealHold?.delete(symbolIndex);
      this.activeAnimations.delete(symbolIndex);
      // The frame goes back to the pool asleep, hidden and in setup pose.
      this.releaseRevealSymbol(symbolIndex, GOLDEN_FRAME_SYMBOL_ID);
      // The lid too: it was faded to nothing, and releaseRevealSymbol restores
      // its pose and puts it to sleep for the next mystery on this row.
      this.releaseRevealSymbol(symbolIndex, MYSTERY_SYMBOL_ID);
    }
    this.goldenControllers.clear();
    this._goldenSettled?.clear();
  }

  /** Tear down every live reveal sequence. Safe to call repeatedly. */
  destroyRevealSymbols() {
    if (this.goldenControllers) {
      for (const controller of this.goldenControllers.values()) controller.destroy();
      this.goldenControllers.clear();
    }
    if (!this.revealControllers) return;
    for (const controller of this.revealControllers.values()) {
      controller.destroy();
    }
    this.revealControllers.clear();
  }
}

export default OverlayReelController;
